(() => {
  if (customElements.get('ivori-product-page')) return;
  function initialize(root) {
    const controller = new AbortController();
    const options = {signal: controller.signal};
    const track = root.querySelector('[data-ivori-track]');
    const slides = [...root.querySelectorAll('[data-ivori-slide]')];
    const thumbs = [...root.querySelectorAll('[data-ivori-thumb]')];
    const thumbTrack = root.querySelector('.ivori-thumbs');
    const counter = root.querySelector('[data-ivori-count]');
    const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    let index = 0;
    let pendingIndex = null;
    let scrollFrame;
    let scrollSettleTimer;
    let galleryScrolling = false;
    let galleryPointer = null;
    let galleryVisible = false;
    let lastTrackLeft = track?.scrollLeft || 0;
    let selectionInitialized = false;
    let trackWidth = 0;
    let slideOffsets = [];
    let thumbGeometry = [];
    let thumbVertical = false;
    let thumbViewport = 0;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileMotionQuery = window.matchMedia('(max-width: 749px)');
    let motionViewerOpen = false;
    function createMotionVideo(image, className) {
      const video = document.createElement('video');
      video.className = className;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.hidden = true;
      video.dataset.ivoriActive = 'false';
      video.tabIndex = -1;
      video.setAttribute('aria-hidden', 'true');
      image.parentElement.classList.add('ivori-motion-container');
      image.after(video);
      video.addEventListener('playing', () => {
        if (video.dataset.ivoriActive === 'true') video.hidden = false;
        else video.pause();
      }, options);
      video.addEventListener('error', () => {
        video.dataset.ivoriFailed = 'true';
        stopMotionVideo(video);
      }, options);
      return video;
    }
    function stopMotionVideo(video, release = false) {
      if (!video) return;
      if (video.dataset.ivoriActive === 'true' || video.dataset.ivoriPlayPending === 'true' || !video.paused) {
        video.dataset.ivoriPlayAttempt = String(Number(video.dataset.ivoriPlayAttempt || 0) + 1);
        video.dataset.ivoriActive = 'false';
        video.dataset.ivoriPlayPending = 'false';
        video.pause();
      }
      if (!video.hidden) video.hidden = true;
      if (release && video.hasAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    }
    function playMotionVideo(video, source, poster) {
      if (!video || !source || video.dataset.ivoriFailed === 'true') return;
      if (video.getAttribute('poster') !== poster) video.poster = poster;
      if (video.getAttribute('src') !== source) {
        stopMotionVideo(video);
        video.src = source;
      }
      video.dataset.ivoriActive = 'true';
      if (!video.paused || video.dataset.ivoriPlayPending === 'true') return;
      const attempt = String(Number(video.dataset.ivoriPlayAttempt || 0) + 1);
      video.dataset.ivoriPlayAttempt = attempt;
      video.dataset.ivoriPlayPending = 'true';
      video.play().then(() => {
        if (video.dataset.ivoriPlayAttempt !== attempt) return;
        video.dataset.ivoriPlayPending = 'false';
        if (video.dataset.ivoriActive !== 'true' || controller.signal.aborted) video.pause();
      }).catch(() => {
        if (video.dataset.ivoriPlayAttempt !== attempt) return;
        video.dataset.ivoriPlayPending = 'false';
        if (video.dataset.ivoriActive === 'true') video.hidden = true;
      });
    }
    const motionImages = [...root.querySelectorAll('[data-ivori-motion]')].map(image => ({
      image, video: image.dataset.ivoriMotionVideo ? createMotionVideo(image, 'ivori-motion-video') : null, visible: false, started: false, slide: image.closest('[data-ivori-slide]'),
      poster: image.getAttribute('src'), srcset: image.getAttribute('srcset'), sizes: image.getAttribute('sizes')
    }));
    function restoreMotionPoster(state) {
      if (state.image.getAttribute('src') !== state.poster) state.image.src = state.poster;
      if (state.srcset && state.image.getAttribute('srcset') !== state.srcset) state.image.setAttribute('srcset', state.srcset);
      if (state.sizes && state.image.getAttribute('sizes') !== state.sizes) state.image.setAttribute('sizes', state.sizes);
    }
    function warmAdjacentMotion() {
      const connection = navigator.connection;
      if (!galleryVisible || galleryScrolling || motionViewerOpen || document.hidden || motionQuery.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '') || !slides.length) return;
      const adjacent = [slides[(index + 1) % slides.length], slides[(index + slides.length - 1) % slides.length]];
      motionImages.forEach(state => {
        if (!state.video || state.video.hasAttribute('src') || state.video.dataset.ivoriFailed === 'true' || !adjacent.includes(state.slide)) return;
        state.video.preload = 'metadata';
        state.video.poster = state.poster;
        state.video.src = state.image.dataset.ivoriMotionVideo;
      });
    }
    function syncMotionImages() {
      motionImages.forEach(state => {
        const visible = state.visible || state.slide && state.started;
        const active = visible && !document.hidden && !motionQuery.matches && !motionViewerOpen && (!state.slide || !galleryScrolling && slides[index] === state.slide);
        const source = mobileMotionQuery.matches ? state.image.dataset.ivoriMotionMobile : state.image.dataset.ivoriMotionDesktop;
        if (active && (state.video ? state.image.dataset.ivoriMotionVideo : source)) state.started = true;
        if (state.video) {
          restoreMotionPoster(state);
          if (active) playMotionVideo(state.video, state.image.dataset.ivoriMotionVideo, state.poster);
          else stopMotionVideo(state.video);
        } else if (active && source) {
          if (state.image.hasAttribute('srcset')) state.image.removeAttribute('srcset');
          if (state.image.hasAttribute('sizes')) state.image.removeAttribute('sizes');
          if (state.image.getAttribute('src') !== source) state.image.src = source;
        } else {
          restoreMotionPoster(state);
        }
        const motionActive = String(active && Boolean(state.video ? state.image.dataset.ivoriMotionVideo : source));
        if (state.image.dataset.ivoriMotionActive !== motionActive) state.image.dataset.ivoriMotionActive = motionActive;
      });
      warmAdjacentMotion();
    }
    const motionObserver = motionImages.length && 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === track) {
          galleryVisible = entry.isIntersecting;
          return;
        }
        const state = motionImages.find(item => item.image === entry.target);
        if (state) state.visible = entry.isIntersecting;
      });
      syncMotionImages();
    }, {threshold: 0.05}) : null;
    motionImages.forEach(state => {
      if (motionObserver) motionObserver.observe(state.image);
      else state.visible = true;
    });
    if (motionObserver && track) motionObserver.observe(track);
    else galleryVisible = true;
    document.addEventListener('visibilitychange', syncMotionImages, options);
    motionQuery.addEventListener('change', syncMotionImages, options);
    mobileMotionQuery.addEventListener('change', syncMotionImages, options);
    function measureGallery() {
      trackWidth = track?.clientWidth || 0;
      const firstSlideLeft = slides[0]?.offsetLeft || 0;
      slideOffsets = slides.map(slide => slide.offsetLeft - firstSlideLeft);
      if (!thumbTrack) return;
      thumbVertical = getComputedStyle(thumbTrack).flexDirection === 'column';
      thumbViewport = thumbVertical ? thumbTrack.clientHeight : thumbTrack.clientWidth;
      const firstThumbStart = thumbVertical ? thumbs[0]?.offsetTop || 0 : thumbs[0]?.offsetLeft || 0;
      thumbGeometry = thumbs.map(thumb => {
        const start = (thumbVertical ? thumb.offsetTop : thumb.offsetLeft) - firstThumbStart;
        return {start, end: start + (thumbVertical ? thumb.offsetHeight : thumb.offsetWidth)};
      });
    }
    const slideLeft = n => slideOffsets[n] || 0;
    function revealThumb() {
      const bounds = thumbGeometry[index];
      if (!thumbTrack || !bounds) return;
      const axis = thumbVertical ? 'top' : 'left';
      const current = thumbVertical ? thumbTrack.scrollTop : thumbTrack.scrollLeft;
      if (bounds.start < current) thumbTrack.scrollTo({[axis]: bounds.start, behavior: motion()});
      else if (bounds.end > current + thumbViewport) thumbTrack.scrollTo({[axis]: bounds.end - thumbViewport, behavior: motion()});
    }
    function update(n = index, force = false) {
      if (selectionInitialized && n === index && !force) return;
      selectionInitialized = true;
      index = n;
      thumbs.forEach((thumb, position) => thumb.setAttribute('aria-current', String(position === index)));
      slides.forEach((slide, position) => {
        slide.setAttribute('aria-hidden', String(position !== index));
        const button = slide.matches('button') ? slide : slide.querySelector('[data-ivori-zoom]');
        if (button) button.tabIndex = position === index ? 0 : -1;
      });
      if (counter) counter.textContent = `${slides.length ? index + 1 : 0} / ${slides.length}`;
      syncMotionImages();
      revealThumb();
    }
    function nearestSlide(left) {
      let nearest = 0;
      let distance = Infinity;
      slideOffsets.forEach((offset, n) => {
        const candidate = Math.abs(left - offset);
        if (candidate < distance) {nearest = n;distance = candidate;}
      });
      return nearest;
    }
    function beginGalleryScroll() {
      if (galleryScrolling) return;
      galleryScrolling = true;
      syncMotionImages();
    }
    function finishGalleryScroll() {
      clearTimeout(scrollSettleTimer);
      if (galleryPointer !== null || controller.signal.aborted) return;
      if (track && track.clientWidth !== trackWidth) {resizeGallery();return;}
      cancelAnimationFrame(scrollFrame);
      scrollFrame = undefined;
      pendingIndex = null;
      if (trackWidth && slides.length) update(nearestSlide(track.scrollLeft));
      galleryScrolling = false;
      syncMotionImages();
    }
    function scheduleGallerySettle() {
      clearTimeout(scrollSettleTimer);
      scrollSettleTimer = setTimeout(finishGalleryScroll, 180);
    }
    function show(n, behavior = motion()) {
      if (!slides.length || !track || !trackWidth) return;
      const next = (n + slides.length) % slides.length;
      pendingIndex = next;
      beginGalleryScroll();
      update(next);
      track.scrollTo({left: slideLeft(next), behavior});
      scheduleGallerySettle();
    }
    root.querySelector('[data-ivori-prev]')?.addEventListener('click', () => show(index - 1), options);
    root.querySelector('[data-ivori-next]')?.addEventListener('click', () => show(index + 1), options);
    thumbs.forEach((thumb, n) => thumb.addEventListener('click', () => show(n), options));
    track?.addEventListener('keydown', event => {
      const keys = {ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: slides.length - 1};
      if (!(event.key in keys)) return;
      event.preventDefault();
      show(keys[event.key]);
    }, options);
    track?.addEventListener('pointerdown', event => {
      galleryPointer = event.pointerId;
      pendingIndex = null;
    }, {...options, passive: true});
    function releaseGalleryPointer(event) {
      if (galleryPointer !== event.pointerId) return;
      galleryPointer = null;
      if (galleryScrolling) scheduleGallerySettle();
    }
    window.addEventListener('pointerup', releaseGalleryPointer, {...options, passive: true});
    window.addEventListener('pointercancel', releaseGalleryPointer, {...options, passive: true});
    track?.addEventListener('wheel', event => {
      if (Math.abs(event.deltaX) < 1 && !event.shiftKey) return;
      pendingIndex = null;
      beginGalleryScroll();
      scheduleGallerySettle();
    }, {...options, passive: true});
    track?.addEventListener('scroll', () => {
      const left = track.scrollLeft;
      if (Math.abs(left - lastTrackLeft) < 0.5) return;
      lastTrackLeft = left;
      beginGalleryScroll();
      scheduleGallerySettle();
      if (scrollFrame !== undefined) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = undefined;
        if (track.clientWidth !== trackWidth) {resizeGallery();return;}
        if (!trackWidth || !slides.length) return;
        const left = track.scrollLeft;
        if (pendingIndex !== null && Math.abs(left - slideLeft(pendingIndex)) > 2) return;
        pendingIndex = null;
        update(nearestSlide(left));
      });
    }, {...options, passive: true});
    track?.addEventListener('scrollend', finishGalleryScroll, {...options, passive: true});
    function resizeGallery() {
      const previousWidth = trackWidth;
      measureGallery();
      if (trackWidth !== previousWidth) show(index, 'instant');
      revealThumb();
    }
    const resizeObserver = track && 'ResizeObserver' in window ? new ResizeObserver(resizeGallery) : null;
    resizeObserver?.observe(track);
    if (thumbTrack) resizeObserver?.observe(thumbTrack);
    window.addEventListener('resize', resizeGallery, options);
    measureGallery();
    update();
    const dialog = root.querySelector('[data-ivori-lightbox]');
    const zoomButtons = [...root.querySelectorAll('[data-ivori-zoom]')];
    const enlarged = dialog?.querySelector('[data-ivori-full-image]');
    const viewerStage = dialog?.querySelector('[data-ivori-lightbox-stage]');
    const viewerCanvas = dialog?.querySelector('[data-ivori-lightbox-canvas]');
    const viewerVideo = enlarged && motionImages.some(state => state.video) ? createMotionVideo(enlarged, 'ivori-viewer-motion-video') : null;
    const viewerToggle = dialog?.querySelector('[data-ivori-lightbox-toggle]');
    const viewerStatus = dialog?.querySelector('[data-ivori-lightbox-status]');
    let viewerIndex = 0;
    let viewerZoomed = false;
    let fullWidth = 0;
    let fullHeight = 0;
    let imageReady = false;
    let viewerRequest = 0;
    let displayedPixels = 0;
    const viewerImages = new Map();
    let pageOverflow;
    let drag = null;
    let suppressImageClick = false;
    function sizeViewer(point) {
      if (!dialog?.open || !viewerStage || !viewerCanvas || !enlarged || !fullWidth || !fullHeight) return;
      const previousWidth = enlarged.getBoundingClientRect().width;
      const stageRect = viewerStage.getBoundingClientRect();
      const x = point ? point.x - stageRect.left : viewerStage.clientWidth / 2;
      const y = point ? point.y - stageRect.top : viewerStage.clientHeight / 2;
      const previousHeight = previousWidth * fullHeight / fullWidth;
      const ratioX = (viewerStage.scrollLeft + x - Math.max(0, (viewerStage.clientWidth - previousWidth) / 2)) / (previousWidth || 1);
      const ratioY = (viewerStage.scrollTop + y - Math.max(0, (viewerStage.clientHeight - previousHeight) / 2)) / (previousHeight || 1);
      const fitWidth = Math.min(viewerStage.clientWidth, viewerStage.clientHeight * fullWidth / fullHeight, fullWidth);
      const canZoom = fullWidth > fitWidth + 1;
      if (!canZoom) viewerZoomed = false;
      const width = viewerZoomed ? Math.min(fullWidth, fitWidth * 2.5) : fitWidth;
      const height = width * fullHeight / fullWidth;
      enlarged.style.width = `${width}px`;
      enlarged.style.height = `${height}px`;
      if (viewerVideo) {
        viewerVideo.style.width = `${width}px`;
        viewerVideo.style.height = `${height}px`;
      }
      viewerCanvas.style.width = `${Math.max(viewerStage.clientWidth, width)}px`;
      viewerCanvas.style.height = `${Math.max(viewerStage.clientHeight, height)}px`;
      viewerCanvas.style.setProperty('--ivori-viewer-width', `${width}px`);
      viewerCanvas.style.setProperty('--ivori-viewer-height', `${height}px`);
      dialog.dataset.zoomed = String(viewerZoomed);
      if (viewerToggle) {
        viewerToggle.disabled = !imageReady || !canZoom;
        viewerToggle.setAttribute('aria-pressed', String(viewerZoomed));
        viewerToggle.textContent = viewerZoomed ? viewerToggle.dataset.fitLabel : viewerToggle.dataset.zoomLabel;
      }
      viewerStage.scrollLeft = viewerZoomed ? ratioX * width + Math.max(0, (viewerStage.clientWidth - width) / 2) - x : 0;
      viewerStage.scrollTop = viewerZoomed ? ratioY * height + Math.max(0, (viewerStage.clientHeight - height) / 2) - y : 0;
    }
    function viewerSource(n, zoomed = false) {
      const button = zoomButtons[(n + zoomButtons.length) % zoomButtons.length];
      const image = button?.querySelector('img');
      if (!image) return null;
      if (image.hasAttribute('data-ivori-motion')) {
        const animate = !motionQuery.matches && !document.hidden;
        const poster = motionImages.find(state => state.image === image)?.poster;
        const video = animate && Boolean(image.dataset.ivoriMotionVideo) && viewerVideo?.dataset.ivoriFailed !== 'true';
        const source = video ? image.dataset.ivoriMotionVideo : animate && !image.dataset.ivoriMotionVideo ? (mobileMotionQuery.matches ? image.dataset.ivoriMotionMobile : image.dataset.ivoriMotionDesktop) : poster;
        const width = Number(image.dataset.ivoriMotionWidth) || (!image.dataset.ivoriMotionVideo && mobileMotionQuery.matches && animate ? 360 : 720);
        const height = Number(image.dataset.ivoriMotionHeight) || width * 1280 / 720;
        return {image, original: source, source, poster, width, height, pixels: width, motion: true, video};
      }
      const original = new URL(button.dataset.ivoriOriginal || image.src || image.currentSrc, window.location.href);
      ['width', 'height', 'crop', 'pad_color'].forEach(key => original.searchParams.delete(key));
      const width = Number(button.dataset.ivoriImageWidth) || image.naturalWidth;
      const height = Number(button.dataset.ivoriImageHeight) || image.naturalHeight;
      const stageWidth = viewerStage?.clientWidth || window.innerWidth - (window.innerWidth > 749 ? 48 : 0);
      const stageHeight = viewerStage?.clientHeight || window.innerHeight - 128;
      const fit = Math.min(stageWidth, stageHeight * width / (height || width), width);
      const pixels = Math.min(width, 3000, Math.ceil(fit * (zoomed ? 2.5 : 1) * Math.min(window.devicePixelRatio || 1, 2)));
      const requested = Math.min(width, [480, 720, 960, 1254, 1536, 1800, 2400, 3000].find(size => size >= pixels) || 3000);
      const source = new URL(original);
      if (/\/(?:files|products)\//.test(source.pathname)) {
        source.searchParams.set('width', String(requested));
        source.searchParams.set('quality', '80');
      }
      return {image, original: original.href, source: source.href, width, height, pixels: requested};
    }
    function loadViewerImage(source, priority) {
      if (viewerImages.has(source)) return viewerImages.get(source);
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = priority;
      const loaded = new Promise((resolve, reject) => {
        image.onload = async () => {
          try {await image.decode();} catch {}
          resolve(image);
        };
        image.onerror = () => {viewerImages.delete(source);reject(new Error('Image unavailable'));};
      });
      viewerImages.set(source, loaded);
      image.src = source;
      return loaded;
    }
    function warmViewerImage(n) {
      const connection = navigator.connection;
      if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      const candidate = viewerSource(n);
      if (candidate?.motion) return;
      if (!candidate || candidate.image.complete && candidate.image.naturalWidth >= candidate.pixels) return;
      loadViewerImage(candidate.source, 'low').catch(() => {});
    }
    function setViewerLoading(loading) {
      dialog.dataset.loading = String(loading);
      if (loading) dialog.setAttribute('aria-busy', 'true');
      else dialog.removeAttribute('aria-busy');
      if (viewerStatus) {
        viewerStatus.dataset.state = 'loading';
        viewerStatus.textContent = viewerStatus.dataset.loading;
        viewerStatus.hidden = !loading;
      }
    }
    function requestViewerImage() {
      const candidate = viewerSource(viewerIndex, viewerZoomed);
      const request = ++viewerRequest;
      if (candidate?.motion) {
        if (candidate.video && viewerVideo) {
          setViewerLoading(false);
          imageReady = true;
          fullWidth = candidate.width;
          fullHeight = candidate.height;
          if (enlarged.getAttribute('src') !== candidate.poster) enlarged.src = candidate.poster;
          playMotionVideo(viewerVideo, candidate.source, candidate.poster);
          sizeViewer();
          return;
        }
        stopMotionVideo(viewerVideo);
        if (enlarged.getAttribute('src') !== candidate.source) {
          setViewerLoading(true);
          enlarged.src = candidate.source;
        }
        return;
      }
      stopMotionVideo(viewerVideo);
      if (!candidate || imageReady && displayedPixels >= candidate.pixels) {
        setViewerLoading(false);
        return;
      }
      setViewerLoading(true);
      loadViewerImage(candidate.source, 'high').then(image => {
        if (controller.signal.aborted || request !== viewerRequest || !dialog.open) return;
        enlarged.src = image.src;
        displayedPixels = image.naturalWidth;
        imageReady = true;
        dialog.dataset.previewReady = 'true';
        setViewerLoading(false);
        sizeViewer();
        warmViewerImage(viewerIndex + 1);
      }).catch(() => {
        if (controller.signal.aborted || request !== viewerRequest || !dialog.open) return;
        setViewerLoading(false);
        if (viewerStatus) {
          viewerStatus.dataset.state = 'error';
          viewerStatus.textContent = viewerStatus.dataset.error;
          viewerStatus.hidden = false;
        }
        sizeViewer();
      });
    }
    function setViewerImage(n) {
      if (!enlarged || !zoomButtons.length) return;
      stopMotionVideo(viewerVideo, true);
      viewerIndex = (n + zoomButtons.length) % zoomButtons.length;
      const button = zoomButtons[viewerIndex];
      const image = button.querySelector('img');
      if (!image) return;
      const candidate = viewerSource(viewerIndex);
      if (!candidate) return;
      ++viewerRequest;
      viewerZoomed = false;
      imageReady = image.complete && image.naturalWidth > 0;
      displayedPixels = imageReady ? image.naturalWidth : 0;
      suppressImageClick = false;
      fullWidth = candidate.motion ? candidate.width : Number(button.dataset.ivoriImageWidth) || image.naturalWidth;
      fullHeight = candidate.motion ? candidate.height : Number(button.dataset.ivoriImageHeight) || image.naturalHeight;
      enlarged.hidden = false;
      dialog.dataset.previewReady = String(imageReady);
      enlarged.alt = image.alt;
      enlarged.removeAttribute('srcset');
      enlarged.removeAttribute('sizes');
      enlarged.src = image.currentSrc || image.src;
      const originalLink = dialog.querySelector('[data-ivori-original-link]');
      if (originalLink) originalLink.href = candidate.original;
      const viewerCount = dialog.querySelector('[data-ivori-lightbox-count]');
      if (viewerCount) viewerCount.textContent = `${viewerIndex + 1} / ${zoomButtons.length}`;
      setViewerLoading(!imageReady);
      const slideIndex = slides.findIndex(slide => slide === button || slide.contains(button));
      if (slideIndex >= 0) show(slideIndex, 'instant');
      sizeViewer();
      if (dialog.open) requestViewerImage();
    }
    enlarged?.addEventListener('load', () => {
      imageReady = true;
      displayedPixels = enlarged.naturalWidth;
      enlarged.hidden = false;
      dialog.dataset.previewReady = 'true';
      if (zoomButtons[viewerIndex]?.querySelector('[data-ivori-motion]')) setViewerLoading(false);
      sizeViewer();
    }, options);
    enlarged?.addEventListener('error', () => {
      imageReady = false;
      dialog.dataset.previewReady = 'false';
      if (dialog.dataset.loading !== 'true' && viewerStatus) {
        viewerStatus.dataset.state = 'error';
        viewerStatus.textContent = viewerStatus.dataset.error;
        viewerStatus.hidden = false;
      }
      sizeViewer();
    }, options);
    zoomButtons.forEach((button, n) => button.addEventListener('click', () => {
      if (!dialog || !enlarged) return;
      motionViewerOpen = true;
      syncMotionImages();
      setViewerImage(n);
      if (!dialog.open) {
        pageOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialog.showModal();
      }
      sizeViewer();
      requestViewerImage();
      warmViewerImage(viewerIndex + 1);
    }, options));
    zoomButtons.forEach((button, n) => {
      button.addEventListener('pointerenter', () => warmViewerImage(n), options);
      button.addEventListener('focus', () => warmViewerImage(n), options);
    });
    root.querySelector('[data-ivori-close]')?.addEventListener('click', () => dialog?.close(), options);
    dialog?.addEventListener('close', () => {
      if (pageOverflow !== undefined) document.body.style.overflow = pageOverflow;
      pageOverflow = undefined;
      motionViewerOpen = false;
      stopMotionVideo(viewerVideo, true);
      if (enlarged && zoomButtons[viewerIndex]?.querySelector('[data-ivori-motion]')) enlarged.removeAttribute('src');
      syncMotionImages();
      ++viewerRequest;
      setViewerLoading(false);
      drag = null;
      dialog.removeAttribute('data-dragging');
      if (root.isConnected) zoomButtons[viewerIndex]?.focus({preventScroll:true});
    }, options);
    function toggleViewer(point) {
      if (!imageReady || viewerToggle?.disabled) return;
      viewerZoomed = !viewerZoomed;
      sizeViewer(point);
      if (viewerZoomed) requestViewerImage();
    }
    viewerToggle?.addEventListener('click', () => toggleViewer(), options);
    enlarged?.addEventListener('click', event => {
      if (suppressImageClick) {suppressImageClick = false;return;}
      toggleViewer({x:event.clientX, y:event.clientY});
    }, options);
    viewerStage?.addEventListener('pointerdown', event => {
      if (!viewerZoomed || event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = {id:event.pointerId, x:event.clientX, y:event.clientY, left:viewerStage.scrollLeft, top:viewerStage.scrollTop};
    }, options);
    viewerStage?.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) {
        suppressImageClick = true;
        if (!viewerStage.hasPointerCapture(event.pointerId)) viewerStage.setPointerCapture(event.pointerId);
        dialog.dataset.dragging = 'true';
      }
      viewerStage.scrollLeft = drag.left - dx;
      viewerStage.scrollTop = drag.top - dy;
    }, options);
    function releaseViewerPointer(event) {
      if (drag?.id !== event.pointerId) return;
      if (viewerStage.hasPointerCapture(event.pointerId)) viewerStage.releasePointerCapture(event.pointerId);
      drag = null;
      dialog.removeAttribute('data-dragging');
    }
    viewerStage?.addEventListener('pointerup', releaseViewerPointer, options);
    viewerStage?.addEventListener('pointercancel', releaseViewerPointer, options);
    dialog?.querySelector('[data-ivori-lightbox-prev]')?.addEventListener('click', () => setViewerImage(viewerIndex - 1), options);
    dialog?.querySelector('[data-ivori-lightbox-next]')?.addEventListener('click', () => setViewerImage(viewerIndex + 1), options);
    dialog?.addEventListener('click', event => {
      if (suppressImageClick) {suppressImageClick = false;return;}
      if (event.target === dialog || (!viewerZoomed && (event.target === viewerStage || event.target === viewerCanvas))) dialog.close();
    }, options);
    dialog?.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        setViewerImage(viewerIndex + (event.key === 'ArrowRight' ? 1 : -1));
      } else if (event.key === '+' || event.key === '=' || event.key === '-') {
        event.preventDefault();
        if (imageReady) {viewerZoomed = event.key !== '-';sizeViewer();if (viewerZoomed) requestViewerImage();}
      }
    }, options);
    function syncViewerMotion() {
      if (!dialog?.open) return;
      if (zoomButtons[viewerIndex]?.querySelector('[data-ivori-motion]')) requestViewerImage();
    }
    document.addEventListener('visibilitychange', syncViewerMotion, options);
    motionQuery.addEventListener('change', syncViewerMotion, options);
    mobileMotionQuery.addEventListener('change', syncViewerMotion, options);
    const viewerResizeObserver = viewerStage && 'ResizeObserver' in window ? new ResizeObserver(() => sizeViewer()) : null;
    viewerResizeObserver?.observe(viewerStage);
    const add = root.querySelector('[data-ivori-add]');
    const stickyAdd = root.querySelector('[data-ivori-sticky-add]');
    const addText = root.querySelector('[data-ivori-add-text]')?.textContent || '';
    const unavailableText = root.querySelector('[data-ivori-unavailable-text]')?.textContent || '';
    const offers = [...root.querySelectorAll('[data-ivori-offer]')];
    const offerAddText = {
      1: root.querySelector('[data-ivori-add-single-text]')?.textContent || addText,
      3: root.querySelector('[data-ivori-add-trio-text]')?.textContent || addText
    };
    const soldOutText = root.querySelector('[data-ivori-sold-out-text]')?.textContent || unavailableText;
    const chooseText = root.querySelector('[data-ivori-choose-text]')?.textContent || '';
    const form = root.querySelector('.ivori-product-form');
    const colorData = root.querySelector('[data-ivori-colors]');
    const colors = colorData ? JSON.parse(colorData.textContent) : [];
    const discountData = root.querySelector('[data-ivori-discount-variants]');
    const discountVariants = discountData ? JSON.parse(discountData.textContent).map(String) : null;
    const nativePack = selection => selection.length === 3 && selection.every(Boolean) ? window.IvoriPacks?.find(selection.map(item => item.id)) : null;
    const hasTrioDiscount = selection => !!nativePack(selection);
    const trioPrice = selection => {
      const pack = window.IvoriPacks?.packs[String(nativePack(selection))];
      return pack ? Number(pack.price) : selection.reduce((sum,item) => sum + Number(item?.price || 0),0);
    };
    const headData = root.querySelector('[data-ivori-heads]');
    const heads = headData ? JSON.parse(headData.textContent) : [];
    const addons = [...root.querySelectorAll('[data-ivori-addon]')];
    const foamData = root.querySelector('[data-ivori-foam]');
    const foam = foamData ? JSON.parse(foamData.textContent) : null;
    const foamRows = [...root.querySelectorAll('[data-ivori-foam-addon]')];
    const addWithExtrasText = root.querySelector('[data-ivori-add-extras-text]')?.textContent || addText;
    const addWithHeadsText = root.querySelector('[data-ivori-add-heads-text]')?.textContent || '';
    const selectors = [...root.querySelectorAll('[data-ivori-device-color]')];
    const swatches = [...root.querySelectorAll('[data-ivori-color]')];
    const sizeMode = root.dataset.ivoriSizeMode === 'true';
    const sizeSelectors = [...root.querySelectorAll('[data-ivori-device-size]')];
    function deviceVariant(select) {
      if (!select) return;
      if (!sizeMode) return colors.find(color => String(color.id) === select.value);
      const size = select.closest('.ivori-bundle-device')?.querySelector('[data-ivori-device-size]')?.value;
      return colors.find(color => color.colour === select.value && color.size === size);
    }
    function syncDeviceSize(select) {
      if (!sizeMode) return;
      const sizeSelect = select.closest('.ivori-bundle-device')?.querySelector('[data-ivori-device-size]');
      if (!sizeSelect) return;
      [...sizeSelect.options].forEach(option => {
        const match = colors.find(item => item.colour === select.value && item.size === option.value);
        const labelVariant = match || colors.find(item => item.size === option.value);
        option.disabled = !match;
        if (labelVariant?.sizeLabel) option.textContent = labelVariant.sizeLabel;
      });
      if (!deviceVariant(select)) {
        const fallback = [...sizeSelect.options].find(option => !option.disabled);
        sizeSelect.value = fallback?.value || '';
        matchHeadToDevice(select);
      }
    }
    function compatibleHead(select) {
      const row = select.closest('.ivori-bundle-card');
      const index = [...row.querySelectorAll('[data-ivori-device-color]')].indexOf(select);
      const headSelect = [...row.querySelectorAll('[data-ivori-head-select]')][index];
      const head = heads.find(item => item.title === deviceVariant(select)?.size);
      return headSelect && head && [...headSelect.options].some(option => option.value === String(head.id)) ? {headSelect, head} : null;
    }
    function matchHeadToDevice(select) {
      if (!sizeMode) return;
      const match = compatibleHead(select);
      if (match) match.headSelect.value = String(match.head.id);
    }
    const mixedText = root.querySelector('[data-ivori-mixed-text]')?.textContent || '';
    const eachText = root.querySelector('[data-ivori-each-text]')?.textContent || '';
    const errorBox = root.querySelector('[data-ivori-cart-error]');
    const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currencyDisplay:'narrowSymbol', currency: root.dataset.currency || 'GBP'}).format(amount / 100);
    let submitting = false;
    let cartNoticeTimer;
    let addConfirmationTimer;
    let confirmedButtons = [];
    function clearAddConfirmation() {
      clearTimeout(addConfirmationTimer);
      if (!confirmedButtons.length) return;
      confirmedButtons.forEach(({button, label, text, disabled}) => {
        delete button.dataset.ivoriAdded;
        label.textContent = text;
        button.disabled = disabled;
      });
      confirmedButtons = [];
      syncBuyNow();
    }
    function showAddConfirmation() {
      clearAddConfirmation();
      if (controller.signal.aborted) return;
      const confirmation = '✓ ' + (checkoutText.addedToCart || 'Added to cart');
      confirmedButtons = [add, stickyAdd].filter(Boolean).map(button => {
        const label = button.querySelector('[data-ivori-add-label]') || button;
        const previous = {button, label, text:label.textContent, disabled:button.disabled};
        button.dataset.ivoriAdded = 'true';
        label.textContent = confirmation;
        button.disabled = true;
        return previous;
      });
      syncBuyNow();
      addConfirmationTimer = setTimeout(clearAddConfirmation,3000);
      openKidsCartAfterAdd();
    }
    form?.addEventListener('input',clearAddConfirmation,options);
    form?.addEventListener('change',clearAddConfirmation,options);
    const cartNotice = document.createElement('p');
    cartNotice.className = 'ivori-cart-success';
    cartNotice.setAttribute('role','status');
    cartNotice.setAttribute('aria-live','polite');
    cartNotice.setAttribute('aria-atomic','true');
    cartNotice.hidden = true;
    if (errorBox) errorBox.insertAdjacentElement('afterend',cartNotice);
    else form?.append(cartNotice);
    controller.signal.addEventListener('abort',() => {clearTimeout(cartNoticeTimer);clearTimeout(addConfirmationTimer);},{once:true});
    async function addItemsInPlace(items) {
      cartNotice.hidden = true;
      clearTimeout(cartNoticeTimer);
      const response = await fetch(root.dataset.cartAddUrl,{
        method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({items:window.IvoriPacks?.toCartItems(items) || items,sections:['cart-icon-bubble'],sections_url:root.dataset.cartUrl}),
        signal:controller.signal
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || root.querySelector('[data-ivori-cart-error-text]')?.textContent);
      preparedSelection = JSON.stringify(items);
      cartNotice.textContent = checkoutText.addedToCart || 'Added to cart';
      cartNotice.hidden = false;
      cartNoticeTimer = setTimeout(() => {cartNotice.hidden = true;},5000);
      try {
        const iconUpdated = updateCartIcon(result.sections);
        let cart = result;
        if (!iconUpdated) {
          const cartResponse = await fetch(`${root.dataset.cartUrl}.js`,{headers:{'Accept':'application/json'},signal:controller.signal});
          if (cartResponse.ok) {cart = await cartResponse.json();updateCartCount(cart);}
        }
        notifyCheckoutCart(cart,'ivori-add-to-cart');
      } catch (error) { /* The items were added even if the header refresh fails. */ }
    }
    function selectedColors() {
      const offer = offers.find(radio => radio.checked);
      return selectors.filter(select => select.dataset.offerQuantity === offer?.value).map(deviceVariant);
    }
    function addonQuantity(input) {
      if (!input) return 1;
      return input.value.trim() === '' ? NaN : Number(input.value);
    }
    function validAddonQuantity(quantity) {
      return Number.isInteger(quantity) && quantity >= 1 && quantity <= 99;
    }
    function headChoices(row) {
      return [...row.querySelectorAll('[data-ivori-head-select]')].map(select => ({
        select,
        head: heads.find(head => String(head.id) === select.value),
        quantity: addonQuantity(select.closest('.ivori-addon-device')?.querySelector('[data-ivori-addon-quantity]'))
      }));
    }
    function headItems(row) {
      return headChoices(row).filter(choice => validAddonQuantity(choice.quantity)).map(choice => choice.head ? {...choice.head, quantity:choice.quantity} : undefined);
    }
    function headQuantitiesValid(row) {
      return headChoices(row).every(choice => validAddonQuantity(choice.quantity) && choice.head);
    }
    function normalizeAddonQuantity(input, commit = false) {
      if (!input) return;
      const quantity = addonQuantity(input);
      if (Number.isFinite(quantity)) {
        const normalized = Math.max(1, Math.min(99, commit ? Math.trunc(quantity) : quantity));
        if (normalized !== quantity) input.value = String(normalized);
      } else if (commit) input.value = '1';
    }
    function normalizeHeadAddonQuantities(row, commit = false) {
      row.querySelectorAll('[data-ivori-addon-quantity]').forEach(input => normalizeAddonQuantity(input, commit));
    }
    function normalizeFoamAddonQuantity(row, commit = false) {
      normalizeAddonQuantity(row.querySelector('[data-ivori-foam-quantity]'), commit);
    }
    function selectedHeads() {
      const offer = offers.find(radio => radio.checked);
      const row = addons.find(item => item.dataset.offerQuantity === offer?.value);
      return row?.querySelector('[data-ivori-addon-toggle]')?.checked ? headItems(row) : [];
    }
    function selectedFoam() {
      const offer = offers.find(radio => radio.checked);
      const row = foamRows.find(item => item.dataset.offerQuantity === offer?.value);
      const quantity = addonQuantity(row?.querySelector('[data-ivori-foam-quantity]'));
      if (!row?.querySelector('[data-ivori-foam-toggle]')?.checked || !validAddonQuantity(quantity)) return [];
      return [foam ? {...foam, quantity} : undefined];
    }
    function groupedItems(variants, includeProperties = true) {
      const grouped = new Map();
      variants.filter(Boolean).forEach(variant => {
        const properties = includeProperties ? variant.properties : undefined;
        const key = `${variant.id}${properties ? `:${JSON.stringify(properties)}` : ''}`;
        if (!grouped.has(key)) grouped.set(key, {id: variant.id, quantity: 0, ...(properties ? {properties} : {})});
        grouped.get(key).quantity += Number(variant.quantity ?? 1);
      });
      return [...grouped.values()];
    }
    function canSupply(variants) {
      return variants.every(Boolean) && groupedItems(variants, false).every(item => {
        const variant = variants.find(value => String(value.id) === String(item.id));
        return variant?.available && (!variant.tracked || variant.policy !== 'deny' || variant.stock >= item.quantity);
      });
    }
    function cartItems() {
      const brushes = selectedColors();
      const pack = nativePack(brushes);
      if (brushes.length === 3 && !pack) return [];
      return groupedItems([...(pack ? [{id:Number(pack),quantity:1}] : brushes), ...selectedHeads(), ...selectedFoam()]);
    }
    function showColor(id) {
      const color = colors.find(item => String(item.id) === String(id));
      const next = slides.findIndex(slide => slide.dataset.mediaId === String(color?.mediaId));
      if (next >= 0) show(next);
      if (color) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', color.id);
        window.history.replaceState({}, '', url);
      }
    }
    function updateOffer() {
      clearAddConfirmation();
      const selected = offers.find(radio => radio.checked);
      if (!selected) return;
      const quantity = Number(selected.value);
      selectors.forEach(syncDeviceSize);
      addons.forEach(row => {
        const compatible = !sizeMode || [...row.closest('.ivori-bundle-card').querySelectorAll('[data-ivori-device-color]')].every(select => compatibleHead(select));
        row.dataset.ivoriHeadsCompatible = String(compatible);
        const toggle = row.querySelector('[data-ivori-addon-toggle]');
        if (toggle) {
          toggle.disabled = !compatible || submitting;
          if (!compatible) toggle.checked = false;
        }
      });
      const selection = selectedColors();
      const valid = selection.length === quantity && selection.every(Boolean) && (quantity === 1 || quantity === 3);
      const values = valid ? selection.map(color => Number(color.brushPrice ?? color.price)) : [];
      const selectedExtras = selectedHeads();
      const selectedFoamExtras = selectedFoam();
      const activeAddon = addons.find(row => row.dataset.offerQuantity === selected.value);
      const activeFoam = foamRows.find(row => row.dataset.offerQuantity === selected.value);
      const extrasValid = !activeAddon?.querySelector('[data-ivori-addon-toggle]')?.checked || (headQuantitiesValid(activeAddon) && selectedExtras.every(Boolean));
      const foamQuantity = addonQuantity(activeFoam?.querySelector('[data-ivori-foam-quantity]'));
      const foamValid = !activeFoam?.querySelector('[data-ivori-foam-toggle]')?.checked || (validAddonQuantity(foamQuantity) && selectedFoamExtras.every(item => item && Number(item.price) > 0));
      const extraAmount = [...selectedExtras, ...selectedFoamExtras].filter(Boolean).reduce((sum, item) => sum + Number(item.price) * Number(item.quantity ?? 1), 0);
      const amount = (quantity === 3 ? trioPrice(selection) : values.reduce((sum,value) => sum + value,0)) + extraAmount;
      const trio = selectors.filter(select => select.dataset.offerQuantity === '3').map(deviceVariant);
      if (trio.length === 3 && trio.every(Boolean)) {
        const discounted = hasTrioDiscount(trio);
        const prices = trio.map(item => Number(item.brushPrice ?? item.price));
        const total = trioPrice(trio);
        const separate = trio.reduce((sum,item) => sum + Number(item.price),0);
        const title = root.querySelector('[data-ivori-trio-title]');
        if (title) title.textContent = root.querySelector(discounted ? '[data-ivori-discount-title-text]' : '[data-ivori-full-trio-title-text]')?.textContent;
        const price = root.querySelector('[data-ivori-trio-price]');
        if (price) price.textContent = money(total);
        const unit = root.querySelector('[data-ivori-trio-unit]');
        if (unit) unit.textContent = eachText.replace('__PRICE__',money(total / 3));
        const saving = root.querySelector('[data-ivori-trio-saving]');
        if (saving) {
          saving.textContent = discounted ? (root.querySelector('[data-ivori-discount-saving-text]')?.textContent || '').replace('__SAVING__',money(separate-total)).replace('__SEPARATE__',money(separate)) : (root.querySelector('[data-ivori-discount-fit-text]')?.textContent || '');
          saving.hidden = !discounted;
        }
        const badge = root.querySelector('[data-ivori-trio-badge]');
        if (badge) badge.hidden = !discounted;
      }
      const single = selectors.filter(select => select.dataset.offerQuantity === '1').map(deviceVariant)[0];
      if (single) {
        const price = root.querySelector('[data-ivori-single-price]');
        if (price) price.textContent = money(Number(single.price));
        const unit = root.querySelector('[data-ivori-single-unit]');
        if (unit) unit.textContent = eachText.replace('__PRICE__',money(Number(single.price)));
      }
      const preview = root.dataset.reviewMode === 'true' || root.dataset.offerReady !== 'true';
      const available = valid && (quantity !== 3 || !!nativePack(selection)) && extrasValid && foamValid && !preview && canSupply([...selection, ...selectedExtras, ...selectedFoamExtras]);
      selectors.forEach(select => {
        const color = deviceVariant(select);
        const sizeSelect = select.closest('.ivori-bundle-device')?.querySelector('[data-ivori-device-size]');
        if (sizeSelect) {
          sizeSelect.disabled = select.dataset.offerQuantity !== selected.value;
        }
        const image = select.closest('.ivori-bundle-device')?.querySelector('img');
        if (image && color?.image) {
          image.removeAttribute('srcset');
          image.src = color.image;
        }
        select.disabled = select.dataset.offerQuantity !== selected.value;
      });
      addons.forEach(row => {
        const active = row.dataset.offerQuantity === selected.value;
        const checked = row.querySelector('[data-ivori-addon-toggle]')?.checked;
        const choices = headChoices(row);
        const items = headItems(row);
        const enabled = active && checked && !submitting;
        row.classList.toggle('is-added', active && checked);
        const panel = row.querySelector('[data-ivori-addon-sizes]');
        if (panel) panel.hidden = !(active && checked);
        choices.forEach(choice => {
          choice.select.disabled = !enabled;
          const device = choice.select.closest('.ivori-addon-device');
          const image = device?.querySelector('img');
          if (image && choice.head?.image) image.src = choice.head.image;
          const quantityInput = device?.querySelector('[data-ivori-addon-quantity]');
          if (quantityInput) quantityInput.disabled = !enabled;
          device?.querySelectorAll('[data-ivori-addon-adjust]').forEach(button => {
            button.disabled = !enabled || (Number(button.dataset.ivoriAddonAdjust) < 0 ? validAddonQuantity(choice.quantity) && choice.quantity <= 1 : validAddonQuantity(choice.quantity) && choice.quantity >= 99);
          });
          const unitPrice = device?.querySelector('[data-ivori-addon-unit-price]');
          if (unitPrice && choice.head) unitPrice.textContent = money(Number(choice.head.price));
          const linePrice = device?.querySelector('[data-ivori-addon-line-price]');
          if (linePrice) linePrice.textContent = money(choice.head && validAddonQuantity(choice.quantity) ? Number(choice.head.price) * choice.quantity : 0);
        });
        const image = row.querySelector('[data-ivori-addon-image]');
        if (image && choices[0]?.head?.image) image.src = choices[0].head.image;
        const total = items.filter(Boolean).reduce((sum, head) => sum + Number(head.price) * head.quantity, 0);
        const price = row.querySelector('[data-ivori-addon-price]');
        if (price) price.textContent = money(total);
        const note = row.querySelector('[data-ivori-addon-note]');
        if (note) note.hidden = preview || !active || (row.dataset.ivoriHeadsCompatible !== 'false' && (!checked || (headQuantitiesValid(row) && canSupply(items))));
      });
      foamRows.forEach(row => {
        const active = row.dataset.offerQuantity === selected.value;
        const checked = row.querySelector('[data-ivori-foam-toggle]')?.checked;
        const quantityInput = row.querySelector('[data-ivori-foam-quantity]');
        const quantity = addonQuantity(quantityInput);
        const validQuantity = validAddonQuantity(quantity);
        const enabled = active && checked && !submitting;
        row.classList.toggle('is-added', active && checked);
        const controls = row.querySelector('[data-ivori-foam-quantity-control]');
        if (controls) controls.hidden = !(active && checked);
        if (quantityInput) quantityInput.disabled = !enabled;
        row.querySelectorAll('[data-ivori-foam-adjust]').forEach(button => {
          button.disabled = !enabled || (Number(button.dataset.ivoriFoamAdjust) < 0 ? validQuantity && quantity <= 1 : validQuantity && quantity >= 99);
        });
        const price = row.querySelector('[data-ivori-foam-price]');
        if (price && foam && Number(foam.price) > 0) price.textContent = money(Number(foam.price) * (validQuantity ? quantity : 0));
        const unitPrice = row.querySelector('[data-ivori-foam-unit-price]');
        if (unitPrice && foam) unitPrice.textContent = money(Number(foam.price));
        const note = row.querySelector('[data-ivori-foam-note]');
        if (note) note.hidden = preview || !(active && checked) || (validQuantity && canSupply([foam ? {...foam, quantity} : undefined]));
      });
      const same = valid && selection.every(color => sizeMode ? color.colour === selection[0].colour : color.id === selection[0].id);
      swatches.forEach(button => button.setAttribute('aria-pressed', String(same && (sizeMode ? selection[0].colour : String(selection[0].id)) === button.dataset.ivoriColor)));
      const colorLabel = root.querySelector('[data-ivori-colour-label]');
      if (colorLabel) colorLabel.textContent = same ? (selection[0].colour || selection[0].title) : mixedText;
      if (form?.querySelector('[data-ivori-current-id]') && selection[0]) form.querySelector('[data-ivori-current-id]').value = selection[0].id;
      root.querySelectorAll('[data-ivori-price], [data-ivori-repeat-price]').forEach(item => {item.textContent = money(amount);});
      if (add) {
        add.disabled = submitting || !available;
        const label = add.querySelector('[data-ivori-add-label]');
        if (label) label.textContent = submitting ? root.querySelector('[data-ivori-adding-text]')?.textContent : preview || available ? selectedFoamExtras.length ? addWithExtrasText : selectedExtras.length ? addWithHeadsText : offerAddText[quantity] : soldOutText;
      }
      if (stickyAdd) {
        stickyAdd.disabled = submitting;
        stickyAdd.textContent = available ? offerAddText[quantity] : chooseText;
      }
    }
    addons.forEach(row => {
      const updateQuantity = (commit = false) => {
        normalizeHeadAddonQuantities(row, commit);
        if (errorBox) errorBox.hidden = true;
        updateOffer();
      };
      row.querySelector('[data-ivori-addon-toggle]')?.addEventListener('change', () => {
        normalizeHeadAddonQuantities(row, true);
        const offer = offers.find(radio => radio.value === row.dataset.offerQuantity);
        if (offer && !offer.checked) offer.click();
        if (errorBox) errorBox.hidden = true;
        updateOffer();
      }, options);
      row.querySelectorAll('[data-ivori-head-select]').forEach(input => input.addEventListener('change', () => {
        if (errorBox) errorBox.hidden = true;
        updateOffer();
      }, options));
      row.querySelectorAll('[data-ivori-addon-quantity]').forEach(input => {
        input.addEventListener('input', () => updateQuantity(), options);
        input.addEventListener('change', () => updateQuantity(true), options);
      });
      row.querySelectorAll('[data-ivori-addon-adjust]').forEach(button => button.addEventListener('click', () => {
        const input = button.closest('.ivori-addon-device')?.querySelector('[data-ivori-addon-quantity]');
        if (!input || button.disabled) return;
        const current = addonQuantity(input);
        input.value = String(Math.max(1, Math.min(99, (validAddonQuantity(current) ? current : 1) + Number(button.dataset.ivoriAddonAdjust))));
        updateQuantity(true);
      }, options));
    });
    foamRows.forEach(row => {
      const updateQuantity = (commit = false) => {
        normalizeFoamAddonQuantity(row, commit);
        if (errorBox) errorBox.hidden = true;
        updateOffer();
      };
      row.querySelector('[data-ivori-foam-toggle]')?.addEventListener('change', () => {
        normalizeFoamAddonQuantity(row, true);
        const offer = offers.find(radio => radio.value === row.dataset.offerQuantity);
        if (offer && !offer.checked) offer.click();
        if (errorBox) errorBox.hidden = true;
        updateOffer();
      }, options);
      row.querySelector('[data-ivori-foam-quantity]')?.addEventListener('input', () => updateQuantity(), options);
      row.querySelector('[data-ivori-foam-quantity]')?.addEventListener('change', () => updateQuantity(true), options);
      row.querySelectorAll('[data-ivori-foam-adjust]').forEach(button => button.addEventListener('click', () => {
        const input = row.querySelector('[data-ivori-foam-quantity]');
        if (!input || button.disabled) return;
        const current = addonQuantity(input);
        input.value = String(Math.max(1, Math.min(99, (validAddonQuantity(current) ? current : 1) + Number(button.dataset.ivoriFoamAdjust))));
        updateQuantity(true);
      }, options));
    });
    swatches.forEach(button => button.addEventListener('click', () => {
      selectors.forEach(select => {select.value = button.dataset.ivoriColor; syncDeviceSize(select); matchHeadToDevice(select);});
      if (errorBox) errorBox.hidden = true;
      updateOffer();
      showColor(selectedColors()[0]?.id);
    }, options));
    selectors.forEach(select => select.addEventListener('change', () => {
      syncDeviceSize(select);
      matchHeadToDevice(select);
      if (errorBox) errorBox.hidden = true;
      updateOffer();
      showColor(deviceVariant(select)?.id);
    }, options));
    sizeSelectors.forEach(select => select.addEventListener('change', () => {
      const colorSelect = select.closest('.ivori-bundle-device')?.querySelector('[data-ivori-device-color]');
      if (colorSelect) matchHeadToDevice(colorSelect);
      if (errorBox) errorBox.hidden = true;
      updateOffer();
      showColor(deviceVariant(colorSelect)?.id);
    }, options));
    offers.forEach(radio => {
      const card = radio.closest('.ivori-bundle-card');
      card?.addEventListener('click', event => {
        if (event.defaultPrevented || radio.disabled || radio.checked) return;
        if (event.target instanceof Element && event.target.closest('input, select, textarea, button, a, label')) return;
        radio.focus({preventScroll: true});
        radio.click();
      }, options);
    });
    offers.forEach(radio => radio.addEventListener('change', () => {
      if (radio.checked) {
        const includeHeads = addons.some(row => row.querySelector('[data-ivori-addon-toggle]')?.checked);
        const includeFoam = foamRows.some(row => row.querySelector('[data-ivori-foam-toggle]')?.checked);
        addons.forEach(row => {
          const toggle = row.querySelector('[data-ivori-addon-toggle]');
          if (toggle) toggle.checked = includeHeads && row.dataset.offerQuantity === radio.value;
          normalizeHeadAddonQuantities(row, true);
        });
        foamRows.forEach(row => {
          const toggle = row.querySelector('[data-ivori-foam-toggle]');
          if (toggle) toggle.checked = includeFoam && !toggle.disabled && row.dataset.offerQuantity === radio.value;
          normalizeFoamAddonQuantity(row, true);
        });
      }
      updateOffer();
      showColor(selectedColors()[0]?.id);
    }, options));
    if (offers.length) {
      if (sizeMode) selectors.forEach(select => {syncDeviceSize(select); matchHeadToDevice(select);});
      updateOffer();
      showColor(selectedColors()[0]?.id);
      form?.addEventListener('submit', async event => {
        event.preventDefault();
        if (submitting) return;
        updateOffer();
        if (!add || add.disabled) return;
        const items = cartItems();
        submitting = true;
        if (errorBox) errorBox.hidden = true;
        updateOffer();
        let added = false;
        try {
          await addItemsInPlace(items);
          added = true;
        } catch (error) {
          if (error.name !== 'AbortError' && errorBox) {
            errorBox.textContent = error.message || root.querySelector('[data-ivori-cart-error-text]')?.textContent;
            errorBox.hidden = false;
          }
        } finally {
          submitting = false;
          updateOffer();
          if (added) showAddConfirmation();
        }
      }, options);
    }
    const headPage = root.dataset.headPdp === 'true';
    const foamPage = root.dataset.ivoriFoamPdp === 'true';
    const variantRadios = [...root.querySelectorAll('[data-ivori-variant]')];
    const headQuantity = root.querySelector('[data-ivori-head-quantity]');
    const headAdjustButtons = [...root.querySelectorAll('[data-ivori-head-adjust]')];
    const foamBrushPair = foamPage ? root.querySelector('[data-ivori-foam-brush-pair]') : null;
    const foamBrushToggle = foamBrushPair?.querySelector('[data-ivori-foam-brush-toggle]');
    const foamBrushSelect = foamBrushPair?.querySelector('[data-ivori-foam-brush-select]');
    const foamBrushQuantity = foamBrushPair?.querySelector('[data-ivori-foam-brush-quantity]');
    const foamBrushAdjustButtons = [...(foamBrushPair?.querySelectorAll('[data-ivori-foam-brush-adjust]') || [])];
    const headBrushPairs = headPage && !foamPage ? [...root.querySelectorAll('[data-ivori-head-brush-pair]')] : [];
    function headBrushSelection(row) {
      const enabled = Boolean(row.querySelector('[data-ivori-head-brush-toggle]')?.checked);
      const colour = row.querySelector('[data-ivori-head-brush-colour]')?.value;
      const size = row.querySelector('[data-ivori-head-brush-size]')?.value;
      const variants = [...(row.querySelector('[data-ivori-head-brush-variants]')?.options || [])];
      const variant = variants.find(item => item.dataset.colour === colour && (!size || item.dataset.size === size));
      const quantity = addonQuantity(row.querySelector('[data-ivori-head-brush-quantity]'));
      const price = Number(variant?.dataset.price);
      const id = Number(variant?.value);
      const valid = validAddonQuantity(quantity) && Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(price) && price >= 0;
      const tracked = variant?.dataset.tracked === 'true' && variant.dataset.policy === 'deny';
      const stock = Number(variant?.dataset.stock);
      const limit = tracked ? Math.min(99, Math.max(1, Number.isFinite(stock) ? stock : 1)) : 99;
      const available = valid && variant?.dataset.available === 'true' && (!tracked || Number.isFinite(stock) && stock >= quantity);
      const freeUnits = enabled && valid && row.dataset.discountEnabled === 'true' ? Math.floor(quantity / 3) : 0;
      return {enabled, variant, variants, colour, size, id, quantity, valid, available, limit, subtotal: enabled && valid ? price * (quantity - freeUnits) : 0};
    }
    function updateHeadBrushPairs() {
      return headBrushPairs.map(row => {
        const selection = headBrushSelection(row);
        const toggle = row.querySelector('[data-ivori-head-brush-toggle]');
        if (toggle) toggle.disabled = submitting || !selection.variants.some(variant => variant.dataset.available === 'true');
        const controls = row.querySelector('[data-ivori-head-brush-controls]');
        if (controls) controls.hidden = !selection.enabled;
        for (const type of ['colour', 'size']) {
          const select = row.querySelector(`[data-ivori-head-brush-${type}]`);
          if (!select) continue;
          select.disabled = submitting || !selection.enabled;
          [...select.options].forEach(option => {
            option.disabled = !selection.variants.some(variant => variant.dataset.available === 'true' && (type === 'colour' ? variant.dataset.colour === option.value && (!selection.size || variant.dataset.size === selection.size) : variant.dataset.size === option.value && variant.dataset.colour === selection.colour));
          });
        }
        const quantity = row.querySelector('[data-ivori-head-brush-quantity]');
        if (quantity) {
          quantity.disabled = submitting || !selection.enabled;
          quantity.readOnly = submitting;
          quantity.max = String(selection.limit);
        }
        row.querySelectorAll('[data-ivori-head-brush-adjust]').forEach(button => {
          const decrement = Number(button.dataset.ivoriHeadBrushAdjust) < 0;
          button.disabled = submitting || !selection.enabled || (decrement ? selection.valid && selection.quantity <= 1 : selection.valid && selection.quantity >= selection.limit);
        });
        const image = row.querySelector('[data-ivori-head-brush-image]');
        const imageUrl = selection.variant?.dataset.image;
        if (image) {
          image.hidden = !imageUrl;
          if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
          image.alt = selection.variant?.dataset.title || '';
        }
        const price = selection.enabled ? selection.subtotal : Number(selection.variant?.dataset.price) || 0;
        row.querySelectorAll('[data-ivori-head-brush-subtotal]').forEach(item => {item.textContent = money(price);});
        row.querySelectorAll('[data-ivori-head-brush-note]').forEach(item => {item.hidden = !selection.enabled || selection.available;});
        return selection;
      });
    }
    function foamBrushSelection() {
      const enabled = Boolean(foamBrushToggle?.checked);
      const variant = foamBrushSelect?.selectedOptions[0];
      const quantity = addonQuantity(foamBrushQuantity);
      const price = Number(variant?.dataset.price);
      const id = Number(variant?.value);
      const valid = Number.isInteger(quantity) && quantity >= 1 && quantity <= 99 && Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(price) && price >= 0;
      const tracked = variant?.dataset.tracked === 'true' && variant.dataset.policy === 'deny';
      const stock = Number(variant?.dataset.stock);
      const limit = tracked ? Math.min(99, Math.max(1, Number.isFinite(stock) ? stock : 1)) : 99;
      const available = valid && variant?.dataset.available === 'true' && (!tracked || Number.isFinite(stock) && stock >= quantity);
      const freeUnits = enabled && valid && foamBrushPair?.dataset.discountEnabled === 'true' ? Math.floor(quantity / 3) : 0;
      return {enabled, variant, id, quantity, valid, available, limit, freeUnits, subtotal: enabled && valid ? price * (quantity - freeUnits) : 0};
    }
    function updateFoamBrushPair() {
      const selection = foamBrushSelection();
      if (!foamBrushPair) return selection;
      if (foamBrushToggle) foamBrushToggle.disabled = submitting;
      const controls = foamBrushPair.querySelector('[data-ivori-foam-brush-controls]');
      if (controls) controls.hidden = !selection.enabled;
      if (foamBrushSelect) foamBrushSelect.disabled = submitting || !selection.enabled;
      if (foamBrushQuantity) {
        foamBrushQuantity.disabled = submitting || !selection.enabled;
        foamBrushQuantity.readOnly = submitting;
        foamBrushQuantity.max = String(selection.limit);
      }
      foamBrushAdjustButtons.forEach(button => {
        const decrement = Number(button.dataset.ivoriFoamBrushAdjust) < 0;
        button.disabled = submitting || !selection.enabled || (decrement ? selection.valid && selection.quantity <= 1 : selection.valid && selection.quantity >= selection.limit);
      });
      const image = foamBrushPair.querySelector('[data-ivori-foam-brush-image]');
      const imageUrl = selection.variant?.dataset.image;
      if (image) {
        image.hidden = !imageUrl;
        if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
        image.alt = selection.variant?.dataset.title || selection.variant?.textContent || '';
      }
      const displayedSubtotal = selection.enabled ? selection.subtotal : Number(selection.variant?.dataset.price) || 0;
      foamBrushPair.querySelectorAll('[data-ivori-foam-brush-subtotal]').forEach(item => {item.textContent = money(displayedSubtotal);});
      foamBrushPair.querySelectorAll('[data-ivori-foam-brush-note]').forEach(item => {item.hidden = !selection.enabled || selection.available;});
      root.querySelectorAll('[data-ivori-foam-estimate-note]').forEach(item => {item.hidden = !selection.freeUnits;});
      return selection;
    }
    function updateHeadPage(moveImage = false) {
      clearAddConfirmation();
      const selected = variantRadios.find(radio => radio.checked);
      if (!selected || !headQuantity) return;
      const quantity = Number(headQuantity.value);
      const valid = Number.isInteger(quantity) && quantity >= 1 && quantity <= 99;
      const quantityLabel = addText.replace('__COUNT__', valid ? quantity : 1);
      const pairedBrush = foamPage ? updateFoamBrushPair() : null;
      const headBrushes = updateHeadBrushPairs();
      const includesBrushes = headBrushes.some(brush => brush.enabled);
      const headBrushTotal = headBrushes.reduce((total, brush) => total + brush.subtotal, 0);
      const pairedLabel = includesBrushes ? root.querySelector('[data-ivori-head-pair-add-text]')?.textContent || quantityLabel : pairedBrush?.enabled ? root.querySelector('[data-ivori-foam-pair-add-text]')?.textContent || quantityLabel : quantityLabel;
      const quantityLimit = selected.dataset.tracked === 'true' && selected.dataset.policy === 'deny' ? Math.min(99, Math.max(1, Number(selected.dataset.stock))) : 99;
      const available = root.dataset.reviewMode !== 'true' && valid && selected.dataset.available === 'true' && (selected.dataset.tracked !== 'true' || selected.dataset.policy !== 'deny' || Number(selected.dataset.stock) >= quantity) && (!pairedBrush?.enabled || pairedBrush.available) && headBrushes.every(brush => !brush.enabled || brush.available);
      headQuantity.readOnly = submitting;
      headAdjustButtons.forEach(button => {
        button.disabled = submitting || (Number(button.dataset.ivoriHeadAdjust) < 0 ? valid && quantity <= 1 : valid && quantity >= quantityLimit);
      });
      root.querySelectorAll('[data-ivori-price]').forEach(item => {item.textContent = money(Number(selected.dataset.price));});
      const headSubtotal = Number(selected.dataset.price) * (valid ? quantity : 0) + (pairedBrush?.subtotal || 0);
      root.querySelectorAll('[data-ivori-head-total], [data-ivori-repeat-price]').forEach(item => {item.textContent = money(headSubtotal + (item.closest('.ivori-repl-quantity') ? 0 : headBrushTotal));});
      root.querySelectorAll('[data-ivori-head-brush-total-row]').forEach(item => {item.hidden = !includesBrushes;});
      if (add) {
        add.disabled = submitting || !available;
        add.querySelector('[data-ivori-add-label]').textContent = submitting ? root.querySelector('[data-ivori-adding-text]')?.textContent : root.dataset.reviewMode === 'true' || available ? pairedLabel : unavailableText;
      }
      if (stickyAdd) {stickyAdd.disabled = submitting;stickyAdd.textContent = available ? pairedLabel : chooseText;}
      if (moveImage) {
        const n = slides.findIndex(slide => slide.dataset.mediaId === selected.dataset.mediaId);
        if (n >= 0) show(n);
        const url = new URL(window.location.href);url.searchParams.set('variant', selected.value);window.history.replaceState({}, '', url);
      }
    }
    variantRadios.forEach(radio => radio.addEventListener('change', () => {
      clearAddConfirmation();
      if (!radio.checked) return;
      if (headPage) {updateHeadPage(true);return;}
      const available = radio.dataset.available === 'true';
      const price = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currencyDisplay:'narrowSymbol', currency: root.dataset.currency || 'GBP'}).format(Number(radio.dataset.price) / 100);
      root.querySelectorAll('[data-ivori-price], [data-ivori-repeat-price]').forEach(item => {item.textContent = price;});
      if (add) {add.disabled = !available; add.querySelector('[data-ivori-add-label]').textContent = available ? addText : unavailableText;}
      if (stickyAdd) stickyAdd.disabled = !available;
    }, options));
    if (foamPage && foamBrushPair) {
      foamBrushToggle?.addEventListener('change', () => {if (!submitting) updateHeadPage();}, options);
      foamBrushSelect?.addEventListener('change', () => {if (!submitting) updateHeadPage();}, options);
      foamBrushQuantity?.addEventListener('input', () => {if (!submitting) updateHeadPage();}, options);
      foamBrushAdjustButtons.forEach(button => button.addEventListener('click', () => {
        if (submitting || !foamBrushQuantity || !foamBrushToggle?.checked) return;
        const selection = foamBrushSelection();
        const next = selection.valid ? selection.quantity + Number(button.dataset.ivoriFoamBrushAdjust) : 1;
        foamBrushQuantity.value = String(Math.min(selection.limit, Math.max(1, next)));
        foamBrushQuantity.dispatchEvent(new Event('input', {bubbles: true}));
      }, options));
    }
    headBrushPairs.forEach(row => {
      const input = row.querySelector('[data-ivori-head-brush-quantity]');
      row.querySelector('[data-ivori-head-brush-toggle]')?.addEventListener('change', () => {if (!submitting) updateHeadPage();}, options);
      row.querySelectorAll('[data-ivori-head-brush-colour], [data-ivori-head-brush-size]').forEach(select => select.addEventListener('change', () => {if (!submitting) updateHeadPage();}, options));
      input?.addEventListener('input', () => {
        if (submitting) return;
        normalizeAddonQuantity(input);
        updateHeadPage();
      }, options);
      input?.addEventListener('change', () => {
        if (submitting) return;
        normalizeAddonQuantity(input, true);
        updateHeadPage();
      }, options);
      row.querySelectorAll('[data-ivori-head-brush-adjust]').forEach(button => button.addEventListener('click', () => {
        if (submitting || !input || !row.querySelector('[data-ivori-head-brush-toggle]')?.checked) return;
        const selection = headBrushSelection(row);
        const next = selection.valid ? selection.quantity + Number(button.dataset.ivoriHeadBrushAdjust) : 1;
        input.value = String(Math.min(selection.limit, Math.max(1, next)));
        input.dispatchEvent(new Event('input', {bubbles:true}));
      }, options));
    });
    if (headPage) {
      headQuantity?.addEventListener('input', () => updateHeadPage(), options);
      headAdjustButtons.forEach(button => button.addEventListener('click', () => {
        if (submitting || !headQuantity) return;
        const selected = variantRadios.find(radio => radio.checked);
        if (!selected) return;
        const limit = selected.dataset.tracked === 'true' && selected.dataset.policy === 'deny' ? Math.min(99, Math.max(1, Number(selected.dataset.stock))) : 99;
        const current = Number(headQuantity.value);
        const next = Number.isInteger(current) && current >= 1 ? current + Number(button.dataset.ivoriHeadAdjust) : 1;
        headQuantity.value = String(Math.min(limit, Math.max(1, next)));
        headQuantity.dispatchEvent(new Event('input', {bubbles: true}));
      }, options));
      updateHeadPage(true);
      form?.addEventListener('submit', async event => {
        event.preventDefault();
        updateHeadPage();
        if (submitting || !add || add.disabled) return;
        const items = purchaseItems();
        if (!items.length) return;
        submitting = true;if (errorBox) errorBox.hidden = true;updateHeadPage();
        let added = false;
        try {
          await addItemsInPlace(items);
          added = true;
        } catch (error) {if(error.name !== 'AbortError' && errorBox){errorBox.textContent=error.message || root.querySelector('[data-ivori-cart-error-text]')?.textContent;errorBox.hidden=false;}}
        finally {submitting=false;updateHeadPage();if(added)showAddConfirmation();}
      }, options);
    }
    const buyNowButtons = [...root.querySelectorAll('[data-ivori-buy-now]')];
    if (form && !offers.length && !headPage) {
      form.addEventListener('submit',async event => {
        event.preventDefault();
        if (submitting || !add || add.disabled) return;
        const items = purchaseItems();
        if (!items.length) return;
        const wasDisabled = add.disabled;
        const label = add.querySelector('[data-ivori-add-label]');
        const previousLabel = label?.textContent;
        submitting = true;
        add.disabled = true;
        if (label) label.textContent = root.querySelector('[data-ivori-adding-text]')?.textContent || previousLabel;
        if (errorBox) errorBox.hidden = true;
        let added = false;
        try {
          await addItemsInPlace(items);
          added = true;
        } catch (error) {
          if (error.name !== 'AbortError' && errorBox) {
            errorBox.textContent = error.message || root.querySelector('[data-ivori-cart-error-text]')?.textContent;
            errorBox.hidden = false;
          }
        } finally {
          submitting = false;
          const selected = variantRadios.find(radio => radio.checked);
          add.disabled = selected ? selected.dataset.available !== 'true' : wasDisabled;
          if (label) label.textContent = add.disabled ? unavailableText : previousLabel;
          syncBuyNow();
          if (added) showAddConfirmation();
        }
      },options);
    }
    const checkoutDialog = root.querySelector('[data-ivori-checkout-dialog]');
    const checkoutContent = checkoutDialog?.querySelector('[data-ivori-checkout-content]');
    const checkoutTextData = checkoutDialog?.querySelector('[data-ivori-checkout-text]');
    const checkoutText = checkoutTextData ? JSON.parse(checkoutTextData.textContent) : {};
    const checkoutStatus = checkoutDialog?.querySelector('[data-ivori-checkout-status]');
    let checkoutOpener;
    let checkoutOverflow;
    let checkoutBusy = false;
    let checkoutOpening = false;
    let preparedSelection;
    let checkoutCartToNotify;
    // Use the visible browser area, including keyboard and in-app browser changes.
    // These are measured viewport dimensions, never fixed device dimensions.
    function syncCheckoutViewport() {
      if (!checkoutDialog) return;
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const width = viewport?.width || window.innerWidth;
      const top = Math.max(0, Math.min(viewport?.offsetTop || 0, window.innerHeight - height));
      const left = Math.max(0, Math.min(viewport?.offsetLeft || 0, window.innerWidth - width));
      checkoutDialog.style.setProperty('--ivori-checkout-view-height', `${height}px`);
      checkoutDialog.style.setProperty('--ivori-checkout-view-width', `${width}px`);
      checkoutDialog.style.setProperty('--ivori-checkout-view-top', `${top}px`);
      checkoutDialog.style.setProperty('--ivori-checkout-view-left', `${left}px`);
    }
    function updateOpenCheckoutViewport() {
      if (checkoutDialog?.open) syncCheckoutViewport();
    }
    window.addEventListener('resize', updateOpenCheckoutViewport, options);
    window.visualViewport?.addEventListener('resize', updateOpenCheckoutViewport, options);
    window.visualViewport?.addEventListener('scroll', updateOpenCheckoutViewport, options);
    function syncBuyNow() {
      const busy = checkoutOpening || checkoutBusy && (checkoutDialog?.open || !checkoutUpdating);
      buyNowButtons.forEach(button => {button.disabled = !add || submitting || (add.disabled && add.dataset.ivoriAdded !== 'true') || busy;});
    }
    function purchaseItems() {
      if (offers.length) return cartItems();
      if (headPage) {
        const selected = variantRadios.find(radio => radio.checked);
        if (!selected) return [];
        const items = [{id:Number(selected.value),quantity:Number(headQuantity.value)}];
        if (foamPage) {
          const pairedBrush = foamBrushSelection();
          if (pairedBrush.enabled) {
            if (!pairedBrush.available) return [];
            items.push({id:pairedBrush.id, quantity:pairedBrush.quantity});
          }
        }
        for (const row of headBrushPairs) {
          const brush = headBrushSelection(row);
          if (!brush.enabled) continue;
          if (!brush.available) return [];
          items.push({id:brush.id, quantity:brush.quantity});
        }
        return window.IvoriPacks?.toCartItems(items) || items;
      }
      const selectedId = new FormData(form).get('id');
      return selectedId ? [{id:Number(selectedId),quantity:Number(new FormData(form).get('quantity') || 1)}] : [];
    }
    function checkoutElement(tag, className, value) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (value) element.textContent = value;
      return element;
    }
    function showCheckoutLoading() {
      const loading = checkoutElement('div','ivori-checkout-loading');
      const list = checkoutElement('ul','ivori-checkout-items');
      list.setAttribute('aria-hidden','true');
      for (let index = 0; index < 3; index++) {
        const row = checkoutElement('li','ivori-checkout-item ivori-checkout-skeleton');
        const copy = checkoutElement('div','ivori-checkout-skeleton-copy');
        const actions = checkoutElement('div','ivori-checkout-item-actions');
        copy.append(checkoutElement('span','ivori-checkout-skeleton-title'),checkoutElement('span','ivori-checkout-skeleton-variant'));
        actions.append(checkoutElement('span','ivori-checkout-skeleton-price'),checkoutElement('span','ivori-checkout-skeleton-quantity'));
        row.append(checkoutElement('span','ivori-checkout-skeleton-image'),copy,actions);
        list.append(row);
      }
      loading.append(list);
      const order = checkoutElement('div','ivori-checkout-order');
      const scroll = checkoutElement('div','ivori-checkout-scroll');
      const footer = checkoutElement('div','ivori-checkout-footer');
      const total = checkoutElement('p','ivori-checkout-total');
      const placeholder = checkoutElement('strong','ivori-checkout-total-placeholder');
      placeholder.setAttribute('aria-hidden','true');
      total.append(checkoutElement('span','',checkoutText.subtotal),placeholder);
      const shipping = checkoutElement('p','ivori-checkout-shipping');
      shipping.append(checkoutElement('span','',checkoutText.shipping),checkoutElement('strong','',checkoutText.free));
      const loadingForm = checkoutElement('form');
      const button = checkoutElement('button','ivori-checkout-submit',checkoutText.checkout);
      button.type = 'submit';
      button.disabled = true;
      loadingForm.append(button,checkoutElement('p','ivori-checkout-guarantee',checkoutText.guarantee));
      if (checkoutText.hasWallets) {
        const wallets = checkoutElement('div','ivori-checkout-wallets ivori-checkout-wallets-placeholder');
        wallets.setAttribute('aria-hidden','true');
        for (let index = 0; index < 4; index++) wallets.append(checkoutElement('span'));
        loadingForm.prepend(wallets);
      }
      const close = checkoutElement('button','ivori-checkout-continue',checkoutText.continue);
      close.type = 'button';
      close.dataset.ivoriCheckoutClose = '';
      footer.append(total,shipping,loadingForm,close);
      scroll.append(loading);
      order.append(scroll,footer);
      checkoutContent.replaceChildren(order);
      if (checkoutStatus) checkoutStatus.textContent = checkoutText.loading;
    }
    function updateCartIcon(sections) {
      const icon = document.getElementById('cart-icon-bubble');
      const section = sections?.['cart-icon-bubble'];
      const rendered = section && new DOMParser().parseFromString(section,'text/html').querySelector('.shopify-section');
      if (!icon || !rendered) return false;
      icon.innerHTML = rendered.innerHTML;
      return true;
    }
    function updateCartCount(cart) {
      const icon = document.getElementById('cart-icon-bubble');
      if (!icon || typeof cart?.item_count !== 'number') return;
      icon.querySelector('.cart-count-bubble')?.remove();
      if (!cart.item_count) return;
      const count = checkoutElement('div','cart-count-bubble');
      if (cart.item_count < 100) {
        const visible = checkoutElement('span','',String(cart.item_count));
        visible.setAttribute('aria-hidden','true');
        count.append(visible);
      }
      count.append(checkoutElement('span','visually-hidden',checkoutText.cartCount?.replace('__COUNT__',cart.item_count)));
      icon.append(count);
    }
    function checkoutFallback(cart,target = checkoutContent) {
      target.replaceChildren();
      const order = checkoutElement('div','ivori-checkout-order');
      order.dataset.ivoriCheckoutOrder = '';
      order.dataset.ivoriCheckoutTotal = cart?.total_price ?? 0;
      order.dataset.ivoriCheckoutCurrency = cart?.currency || root.dataset.currency || 'GBP';
      const scroll = checkoutElement('div','ivori-checkout-scroll');
      const footer = checkoutElement('div','ivori-checkout-footer');
      order.append(scroll,footer);
      target.append(order);
      const currency = cart?.currency || root.dataset.currency || 'GBP';
      const format = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency',currencyDisplay:'narrowSymbol',currency}).format(amount / 100);
      if (cart?.items) {
        const list = checkoutElement('ul','ivori-checkout-items');
        cart.items.forEach(item => {
          const row = checkoutElement('li','ivori-checkout-item');
          row.dataset.ivoriCheckoutKey = item.key;
          row.dataset.ivoriCheckoutQuantity = item.quantity;
          row.dataset.ivoriCheckoutLinePrice = item.final_line_price ?? item.line_price;
          row.dataset.ivoriCheckoutOriginalPrice = item.original_line_price ?? item.final_line_price ?? item.line_price;
          row.dataset.ivoriCheckoutVariant = item.variant_id || item.id;
          row.dataset.ivoriCheckoutProperties = JSON.stringify(item.properties || {});
          row.dataset.ivoriCheckoutPlan = item.selling_plan_allocation?.selling_plan?.id || '';
          const pack = window.IvoriPacks?.packs[String(item.variant_id || item.id)];
          if (pack) row.classList.add('ivori-checkout-pack');
          const imageUrl = item.featured_image?.url || item.image;
          if (imageUrl && !pack) {
            const image = checkoutElement('img');
            image.src = imageUrl;
            image.alt = item.product_title || item.title;
            image.width = 56;
            image.height = 56;
            image.loading = 'lazy';
            row.append(image);
          }
          const copy = checkoutElement('div','ivori-checkout-item-copy');
          copy.append(checkoutElement('strong','',item.product_title || item.title));
          if (pack) {
            const images = checkoutElement('div','ivori-pack-images');
            pack.choices.forEach(id => {
              const choice = window.IvoriPacks.choices[id];
              const image = checkoutElement('img');
              image.src = choice.image; image.alt = choice.title; image.width = 48; image.height = 48;
              images.append(image);
            });
            copy.append(images, checkoutElement('p','ivori-pack-contents',pack.contents.replaceAll(' / Adult',' / Ages 13+')),
              checkoutElement('p','ivori-pack-saving',Number(pack.comparePrice) > Number(pack.price) ? (checkoutText.packOfferSaving || checkoutText.packOffer).replace('__SAVING__',format(pack.comparePrice-pack.price)) : checkoutText.packOffer));
          }
          const variantTitle = Number(item.product_id) === Number(checkoutText.headProductId) && item.variant_title === 'Adult' ? checkoutText.headSizeLabel : item.variant_title;
          if (!pack && variantTitle && variantTitle !== checkoutText.defaultVariant) copy.append(checkoutElement('small','',variantTitle));
          Object.entries(item.properties || {}).forEach(([name,value]) => {
            if (value && !name.startsWith('_')) copy.append(checkoutElement('small','',`${name}: ${value}`));
          });
          const itemLabel = `${item.product_title || item.title}${variantTitle && variantTitle !== checkoutText.defaultVariant ? ` · ${variantTitle}` : ''}`;
          const edit = checkoutElement('div','ivori-checkout-edit');
          const quantity = checkoutElement('div','ivori-checkout-quantity');
          const input = checkoutElement('input');
          input.type = 'number';
          input.min = '0';
          input.step = '1';
          input.inputMode = 'numeric';
          input.value = item.quantity;
          input.dataset.ivoriCheckoutInput = '';
          input.setAttribute('aria-label',checkoutText.quantityLabel?.replace('__ITEM__',itemLabel));
          [-1,1].forEach(adjustment => {
            const button = checkoutElement('button','',adjustment < 0 ? '−' : '+');
            button.type = 'button';
            button.dataset.ivoriCheckoutAdjust = adjustment;
            button.setAttribute('aria-label',(adjustment < 0 ? checkoutText.decrease : checkoutText.increase)?.replace('__ITEM__',itemLabel));
            quantity.append(button);
            if (adjustment < 0) quantity.append(input);
          });
          const remove = checkoutElement('button','ivori-checkout-remove',checkoutText.remove);
          remove.type = 'button';
          remove.dataset.ivoriCheckoutRemove = '';
          remove.setAttribute('aria-label',checkoutText.removeLabel?.replace('__ITEM__',itemLabel));
          edit.append(quantity);
          copy.append(remove);
          const linePrice = item.final_line_price ?? item.line_price;
          const price = checkoutElement('span','ivori-checkout-line-price');
          if (item.original_line_price > linePrice) price.append(checkoutElement('s','',format(item.original_line_price)));
          price.append(document.createTextNode(format(linePrice)));
          const actions = checkoutElement('div','ivori-checkout-item-actions');
          actions.append(price,edit);
          row.append(copy,actions);
          list.append(row);
        });
        scroll.append(list);
        if (!cart.item_count) scroll.append(checkoutElement('p','ivori-checkout-note',checkoutText.empty));
        (cart.cart_level_discount_applications || []).forEach(discount => {
          scroll.append(checkoutElement('p','ivori-checkout-discount',`${discount.title} · −${format(discount.total_allocated_amount)}`));
        });
        const total = checkoutElement('p','ivori-checkout-total');
        total.append(checkoutElement('span','',checkoutText.subtotal),checkoutElement('strong','',format(cart.total_price)));
        const shipping = checkoutElement('p','ivori-checkout-shipping');
        shipping.append(checkoutElement('span','',checkoutText.shipping),checkoutElement('strong','',checkoutText.free));
        footer.append(total,shipping);
      } else scroll.append(checkoutElement('p','ivori-checkout-note',checkoutText.readyFallback));
      const checkoutForm = checkoutElement('form');
      checkoutForm.method = 'post';
      checkoutForm.action = root.dataset.cartUrl;
      const checkoutButton = checkoutElement('button','ivori-checkout-submit',checkoutText.checkout);
      checkoutButton.type = 'submit';
      checkoutButton.name = 'checkout';
      checkoutButton.disabled = !cart?.item_count;
      checkoutForm.append(checkoutButton, checkoutElement('p','ivori-checkout-guarantee',checkoutText.guarantee));
      const close = checkoutElement('button','ivori-checkout-continue',checkoutText.continue);
      close.type = 'button';
      close.dataset.ivoriCheckoutClose = '';
      footer.append(checkoutForm,close);
      return order;
    }
    async function loadCheckoutFallback() {
      try {
        const response = await fetch(`${root.dataset.cartUrl}.js`,{headers:{'Accept':'application/json'},signal:controller.signal});
        if (!response.ok) throw new Error();
        const cart = await response.json();
        updateCartCount(cart);
        checkoutFallback(cart);
      } catch (error) {
        if (error.name !== 'AbortError') checkoutFallback();
      }
    }

    function openKidsCartAfterAdd() {
      if (!root.classList.contains('ivori-kids') ||
          !checkoutDialog || !checkoutContent || checkoutDialog.open ||
          checkoutBusy || controller.signal.aborted) return;
      checkoutOpener = add;
      checkoutOverflow = document.body.style.overflow;
      checkoutDialog.classList.toggle('ivori-add-to-cart-drawer', window.matchMedia('(min-width: 990px)').matches);
      syncCheckoutViewport();
      try {
        checkoutDialog.showModal();
      } catch (error) {
        checkoutDialog.classList.remove('ivori-add-to-cart-drawer');
        checkoutOverflow = undefined;
        return;
      }
      document.body.style.overflow = 'hidden';
      clearTimeout(cartNoticeTimer);
      cartNotice.hidden = true;
      showCheckoutLoading();
      checkoutContent.setAttribute('aria-busy','true');
      checkoutBusy = true;
      syncBuyNow();
      const modalController = new AbortController();
      checkoutDialog.addEventListener('close', () => modalController.abort(), {once: true});
      controller.signal.addEventListener('abort', () => modalController.abort(), {once: true});
      (async () => {
        try {
          const response = await fetch(`${root.dataset.cartUrl}?sections=ivori-quick-checkout`,{
            signal: modalController.signal
          });
          if (!response.ok) throw new Error('Cart section unavailable');
          const sections = await response.json();
          if (modalController.signal.aborted) return;
          const html = sections?.['ivori-quick-checkout'];
          const order = html && new DOMParser().parseFromString(html,'text/html').querySelector('[data-ivori-checkout-order]');
          if (order?.querySelector('form')) checkoutContent.replaceChildren(order);
          else await loadCheckoutFallback();
        } catch (error) {
          if (error.name !== 'AbortError' && !modalController.signal.aborted) await loadCheckoutFallback();
        } finally {
          checkoutBusy = false;
          checkoutContent.setAttribute('aria-busy','false');
          if (checkoutStatus) checkoutStatus.textContent = '';
          syncBuyNow();
        }
      })();
    }
    function notifyCheckoutCart(result,source = 'ivori-buy-now') {
      if (result?.item_count === 0) preparedSelection = undefined;
      if (source === 'ivori-buy-now' && (checkoutDialog?.open || checkoutUpdating)) {
        checkoutCartToNotify = result;
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate,{source,cartData:result})).catch(() => {});
      }
      document.dispatchEvent(new CustomEvent('cart:refresh',{detail:{source}}));
    }
    function notifyClosedCheckout() {
      if (checkoutDialog?.open || checkoutUpdating || !checkoutCartToNotify) return;
      const cart = checkoutCartToNotify;
      checkoutCartToNotify = undefined;
      notifyCheckoutCart(cart);
    }
    const checkoutEdits = new Map();
    let checkoutUpdating = false;
    let checkoutUpdateRunning = false;
    let checkoutUpdateTimer;
    let checkoutUpdatePromise;
    let checkoutHandoff = false;
    let checkoutPreparation;
    let checkoutUpdateError;
    const checkoutProceedForms = new WeakSet();
    const checkoutWalletSources = new Map();
    function setCheckoutEditing(busy) {
      checkoutBusy = busy || checkoutHandoff;
      checkoutContent.setAttribute('aria-busy',String(busy));
      syncBuyNow();
    }
    function setCheckoutHandoff(active) {
      checkoutHandoff = active;
      checkoutBusy = active || checkoutUpdating;
      if (!active) checkoutPreparation = undefined;
      const order = checkoutContent.querySelector('[data-ivori-checkout-order]');
      order?.toggleAttribute('data-ivori-checkout-opening',active);
      checkoutContent.querySelectorAll('[data-ivori-checkout-key]').forEach(row => syncCheckoutPending(row));
      checkoutContent.querySelectorAll('.ivori-checkout-submit').forEach(button => {
        button.disabled = active || !checkoutContent.querySelector('[data-ivori-checkout-key]');
        button.textContent = active ? checkoutText.opening : checkoutText.checkout;
      });
      checkoutContent.querySelectorAll('.ivori-checkout-wallets').forEach(wallet => {wallet.inert = active;});
      if (active && checkoutStatus) checkoutStatus.textContent = checkoutText.opening;
      syncBuyNow();
    }
    function renderCheckoutPrices() {
      const order = checkoutContent.querySelector('[data-ivori-checkout-order]');
      if (!order || !('ivoriCheckoutTotal' in order.dataset)) return;
      let total = Number(order.dataset.ivoriCheckoutTotal);
      const currency = order.dataset.ivoriCheckoutCurrency || root.dataset.currency || 'GBP';
      const format = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-GB',{style:'currency',currencyDisplay:'narrowSymbol',currency}).format(amount / 100);
      order.querySelectorAll('[data-ivori-checkout-key]').forEach(row => {
        const confirmedQuantity = Number(row.dataset.ivoriCheckoutQuantity);
        const linePrice = Number(row.dataset.ivoriCheckoutLinePrice);
        const originalPrice = Number(row.dataset.ivoriCheckoutOriginalPrice);
        if (!confirmedQuantity || !Number.isFinite(linePrice) || !Number.isFinite(originalPrice)) return;
        const input = row.querySelector('[data-ivori-checkout-input]');
        const draft = input.value === '' ? NaN : Number(input.value);
        const quantity = Number.isSafeInteger(draft) && draft >= 0 ? draft : checkoutEdits.get(row)?.quantity ?? confirmedQuantity;
        const projectedPrice = Math.round(linePrice * quantity / confirmedQuantity);
        total += projectedPrice - linePrice;
        const price = row.querySelector('.ivori-checkout-line-price');
        if (!price) return;
        let comparison = price.querySelector('s');
        if (originalPrice > linePrice) {
          if (!comparison) {
            comparison = checkoutElement('s');
            price.prepend(comparison);
          }
          const value = format(Math.round(originalPrice * quantity / confirmedQuantity));
          if (comparison.textContent !== value) comparison.textContent = value;
        } else comparison?.remove();
        const value = format(projectedPrice);
        if (price.lastChild?.nodeType === 3) {
          if (price.lastChild.nodeValue !== value) price.lastChild.nodeValue = value;
        } else price.append(document.createTextNode(value));
      });
      const subtotal = order.querySelector('.ivori-checkout-total strong');
      const value = format(Math.max(0,total));
      if (subtotal && subtotal.textContent !== value) subtotal.textContent = value;
    }
    function checkoutRowIdentity(row) {
      if (!row.dataset.ivoriCheckoutVariant) return row.dataset.ivoriCheckoutKey;
      let properties = {};
      try {properties = JSON.parse(row.dataset.ivoriCheckoutProperties || '{}') || {};} catch (error) {}
      return JSON.stringify([row.dataset.ivoriCheckoutVariant,row.dataset.ivoriCheckoutPlan || '',Object.entries(properties).sort(([a],[b]) => a.localeCompare(b))]);
    }
    function syncCheckoutPending(row,preserveInput = false) {
      const edit = checkoutEdits.get(row);
      if (edit) {
        row.dataset.ivoriCheckoutPending = edit.quantity === 0 ? 'removing' : 'updating';
        row.setAttribute('aria-busy','true');
        if (!preserveInput) row.querySelector('[data-ivori-checkout-input]').value = edit.quantity;
        row.dataset.ivoriCheckoutRequested = edit.quantity;
      } else {
        delete row.dataset.ivoriCheckoutPending;
        delete row.dataset.ivoriCheckoutRequested;
        row.removeAttribute('aria-busy');
      }
      row.querySelectorAll('button,input').forEach(control => {
        control.disabled = checkoutHandoff || edit?.quantity === 0;
      });
    }
    function reconcileCheckout(cart) {
      if (preparedSelection && Array.isArray(cart.items)) {
        const selectedItems = JSON.parse(preparedSelection);
        const preparedItems = window.IvoriPacks?.toCartItems(selectedItems) || selectedItems;
        const identity = item => JSON.stringify([
          String(item.variant_id || item.id),
          String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || ''),
          Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b))
        ]);
        if (preparedItems.some(item => !cart.items.some(current =>
          identity(current) === identity(item) && current.quantity >= item.quantity))) preparedSelection = undefined;
      }
      const section = cart.sections?.['ivori-quick-checkout'];
      let rendered = section && new DOMParser().parseFromString(section,'text/html').querySelector('[data-ivori-checkout-order]');
      if (!rendered?.querySelector('form')) rendered = checkoutFallback(cart,document.createElement('div'));
      const current = checkoutContent.querySelector('[data-ivori-checkout-order]');
      const list = current?.querySelector('.ivori-checkout-items');
      if (!current || !list) {
        checkoutContent.replaceChildren(rendered);
        return;
      }
      const scroll = current.querySelector('.ivori-checkout-scroll');
      const scrollTop = scroll.scrollTop;
      const oldRows = [...list.querySelectorAll('[data-ivori-checkout-key]')];
      const used = new Set();
      const incomingRows = [...rendered.querySelectorAll('[data-ivori-checkout-key]')];
      incomingRows.forEach((incoming,index) => {
        const row = oldRows.find(candidate => !used.has(candidate) && candidate.dataset.ivoriCheckoutKey === incoming.dataset.ivoriCheckoutKey)
          || oldRows.find(candidate => !used.has(candidate) && checkoutRowIdentity(candidate) === checkoutRowIdentity(incoming));
        if (!row) {
          list.insertBefore(incoming,list.children[index] || null);
          return;
        }
        used.add(row);
        const input = row.querySelector('[data-ivori-checkout-input]');
        const editing = document.activeElement === input && input.value !== (row.dataset.ivoriCheckoutRequested ?? row.dataset.ivoriCheckoutQuantity);
        for (const name of ['ivoriCheckoutKey','ivoriCheckoutQuantity','ivoriCheckoutVariant','ivoriCheckoutProperties','ivoriCheckoutPlan','ivoriCheckoutLinePrice','ivoriCheckoutOriginalPrice']) {
          if (name in incoming.dataset) row.dataset[name] = incoming.dataset[name];
        }
        for (const selector of ['.ivori-checkout-item-copy','.ivori-checkout-line-price']) {
          const original = row.querySelector(selector);
          const replacement = incoming.querySelector(selector);
          if (original && replacement && original.innerHTML !== replacement.innerHTML) original.replaceChildren(...replacement.childNodes);
        }
        if (!checkoutEdits.has(row) && !editing) input.value = incoming.dataset.ivoriCheckoutQuantity;
        syncCheckoutPending(row,editing);
        if (list.children[index] !== row) list.insertBefore(row,list.children[index] || null);
      });
      oldRows.filter(row => !used.has(row)).forEach(row => {
        checkoutEdits.delete(row);
        const hadFocus = row.contains(document.activeElement) || row.dataset.ivoriCheckoutPending === 'removing' && document.activeElement === document.body;
        const index = oldRows.indexOf(row);
        row.remove();
        if (hadFocus && checkoutDialog.open) {
          (list.children[Math.min(index,list.children.length - 1)]?.querySelector('[data-ivori-checkout-adjust="1"]')
            || current.querySelector('[data-ivori-checkout-close]'))?.focus({preventScroll:true});
        }
      });
      [...scroll.children].filter(child => child !== list).forEach(child => child.remove());
      [...rendered.querySelector('.ivori-checkout-scroll').children].filter(child => !child.matches('.ivori-checkout-items')).forEach(child => scroll.append(child));
      for (const selector of ['.ivori-checkout-total','.ivori-checkout-shipping']) {
        const original = current.querySelector(selector);
        const replacement = rendered.querySelector(selector);
        if (original && replacement && original.innerHTML !== replacement.innerHTML) original.replaceChildren(...replacement.childNodes);
      }
      current.dataset.ivoriCheckoutTotal = cart.total_price;
      current.dataset.ivoriCheckoutCurrency = cart.currency || rendered.dataset.ivoriCheckoutCurrency || root.dataset.currency || 'GBP';
      renderCheckoutPrices();
      const checkoutButton = current.querySelector('.ivori-checkout-submit');
      if (checkoutButton) {
        checkoutButton.disabled = checkoutHandoff || !cart.item_count;
      }
      const wallets = current.querySelector('.ivori-checkout-wallets');
      if (!cart.item_count) wallets?.remove();
      else if (!wallets) {
        const incomingWallets = rendered.querySelector('.ivori-checkout-wallets');
        if (incomingWallets) {
          incomingWallets.inert = checkoutHandoff;
          current.querySelector('form').prepend(incomingWallets);
        }
      }
      scroll.scrollTop = scrollTop;
      updateCartCount(cart);
      notifyCheckoutCart(cart);
    }
    function flushCheckoutEdits() {
      clearTimeout(checkoutUpdateTimer);
      if (checkoutUpdateRunning) return checkoutUpdatePromise;
      if (controller.signal.aborted) return Promise.resolve(false);
      checkoutUpdateRunning = true;
      checkoutUpdatePromise = runCheckoutEdits();
      return checkoutUpdatePromise;
    }
    async function runCheckoutEdits() {
      let failed = false;
      try {
        while (checkoutEdits.size && !controller.signal.aborted) {
          const [row,edit] = checkoutEdits.entries().next().value;
          const key = row.dataset.ivoriCheckoutKey;
          const removalFeedback = edit.quantity === 0 ? new Promise(resolve => setTimeout(resolve,350)) : null;
          const response = await fetch(`${root.dataset.cartUrl}/change.js`,{
            method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},
            body:JSON.stringify({id:key,quantity:edit.quantity,sections:['ivori-quick-checkout'],sections_url:root.dataset.cartUrl}),signal:controller.signal
          });
          const cart = await response.json();
          if (!response.ok) throw new Error(cart.description || cart.message || checkoutText.updateError);
          if (removalFeedback) await removalFeedback;
          if (checkoutEdits.get(row) === edit) checkoutEdits.delete(row);
          reconcileCheckout(cart);
          if (checkoutStatus) checkoutStatus.textContent = checkoutHandoff ? checkoutText.opening : checkoutText.updated?.replace('__COUNT__',cart.item_count);
        }
      } catch (error) {
        failed = true;
        checkoutUpdateError = error.message || checkoutText.updateError;
        checkoutEdits.clear();
        checkoutContent.querySelectorAll('[data-ivori-checkout-key]').forEach(row => {
          row.querySelector('[data-ivori-checkout-input]').value = row.dataset.ivoriCheckoutQuantity;
          syncCheckoutPending(row);
        });
        renderCheckoutPrices();
        if (error.name !== 'AbortError') {
          try {
            const response = await fetch(`${root.dataset.cartUrl}.js`,{headers:{'Accept':'application/json'},signal:controller.signal});
            if (response.ok) reconcileCheckout(await response.json());
          } catch (refreshError) {}
          const message = checkoutElement('p','ivori-checkout-error',error.message || checkoutText.updateError);
          message.setAttribute('role','alert');
          checkoutContent.prepend(message);
          if (checkoutStatus) checkoutStatus.textContent = '';
        }
      } finally {
        checkoutUpdateRunning = false;
        checkoutUpdating = false;
        setCheckoutEditing(false);
        notifyClosedCheckout();
        if (!failed && checkoutStatus) checkoutStatus.textContent = checkoutStatus.textContent || checkoutText.updated;
      }
      return !failed && !controller.signal.aborted;
    }
    function changeCheckoutItem(control,quantity) {
      const row = control.closest('[data-ivori-checkout-key]');
      if (!row?.dataset.ivoriCheckoutKey || checkoutHandoff || (checkoutBusy && !checkoutUpdating)) return;
      const previous = Number(row.dataset.ivoriCheckoutQuantity);
      const input = row.querySelector('[data-ivori-checkout-input]');
      if (!Number.isSafeInteger(quantity) || quantity < 0) {
        input.value = checkoutEdits.get(row)?.quantity ?? previous;
        renderCheckoutPrices();
        return;
      }
      if (quantity === (checkoutEdits.get(row)?.quantity ?? previous)) {
        renderCheckoutPrices();
        return;
      }
      checkoutContent.querySelector('.ivori-checkout-error')?.remove();
      checkoutUpdateError = undefined;
      checkoutEdits.set(row,{quantity});
      checkoutUpdating = true;
      syncCheckoutPending(row);
      renderCheckoutPrices();
      if (checkoutStatus) checkoutStatus.textContent = checkoutText.updating;
      setCheckoutEditing(true);
      clearTimeout(checkoutUpdateTimer);
      if (!checkoutUpdateRunning) checkoutUpdateTimer = setTimeout(flushCheckoutEdits,140);
    }
    function prepareCheckout() {
      if (checkoutPreparation) return checkoutPreparation;
      const inputs = [...checkoutContent.querySelectorAll('[data-ivori-checkout-input]')];
      const invalid = inputs.find(input => input.value === '' || !Number.isSafeInteger(Number(input.value)) || Number(input.value) < 0);
      if (invalid) {
        checkoutUpdateError = checkoutText.quantityError;
        invalid.setCustomValidity(checkoutText.quantityError);
        invalid.reportValidity();
        invalid.focus({preventScroll:true});
        return Promise.resolve(false);
      }
      inputs.forEach(input => changeCheckoutItem(input,Number(input.value)));
      setCheckoutHandoff(true);
      checkoutPreparation = flushCheckoutEdits().then(saved => saved && checkoutDialog.open && !!checkoutContent.querySelector('[data-ivori-checkout-key]'));
      return checkoutPreparation;
    }
    async function continueCheckout(checkoutForm) {
      if (checkoutHandoff) return;
      let continued = false;
      try {
        if (!await prepareCheckout() || !checkoutForm.isConnected) return;
        if (root.classList.contains('ivori-kids') && root.dataset.checkoutUrl) {
          location.assign(root.dataset.checkoutUrl);
          continued = true;
          return;
        }
        const button = checkoutForm.querySelector('.ivori-checkout-submit');
        if (!button) return;
        button.disabled = false;
        checkoutProceedForms.add(checkoutForm);
        checkoutForm.requestSubmit(button);
        continued = true;
      } finally {
        if (!continued) setCheckoutHandoff(false);
      }
    }
    function syncCheckoutWallets() {
      if (!checkoutContent) return;
      checkoutContent.querySelectorAll('shopify-accelerated-checkout-cart').forEach(wallet => {
        const source = wallet.dataSource;
        if (!source || typeof source.getInitialCart !== 'function' || checkoutWalletSources.get(source)?.wrapper === source.getInitialCart) return;
        const original = source.getInitialCart;
        const wrapper = async function(...args) {
          try {
            if (!await prepareCheckout()) return {cart:null,errors:[{code:'VALIDATION_CUSTOM',message:checkoutUpdateError || checkoutText.empty}]};
            return await original.apply(this,args);
          } finally {
            setCheckoutHandoff(false);
          }
        };
        checkoutWalletSources.set(source,{original,wrapper});
        source.getInitialCart = wrapper;
      });
    }
    const checkoutWalletObserver = checkoutContent && new MutationObserver(syncCheckoutWallets);
    checkoutWalletObserver?.observe(checkoutContent,{childList:true,subtree:true});
    document.addEventListener('shopify:payment_button:loaded',syncCheckoutWallets,options);
    controller.signal.addEventListener('abort',() => {
      clearTimeout(checkoutUpdateTimer);
      checkoutWalletObserver?.disconnect();
      checkoutWalletSources.forEach(({original,wrapper},source) => {
        if (source.getInitialCart === wrapper) source.getInitialCart = original;
      });
    },{once:true});
    checkoutContent?.addEventListener('click',event => {
      const wallet = event.target.closest('.ivori-checkout-wallets shopify-accelerated-checkout-cart');
      if (!wallet) return;
      syncCheckoutWallets();
      if (wallet.dataSource && checkoutWalletSources.get(wallet.dataSource)?.wrapper === wallet.dataSource.getInitialCart) return;
      const changed = checkoutUpdating || [...checkoutContent.querySelectorAll('[data-ivori-checkout-input]')].some(input => input.value !== input.closest('[data-ivori-checkout-key]').dataset.ivoriCheckoutQuantity);
      if (!changed) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      continueCheckout(wallet.closest('form'));
    },{...options,capture:true});
    checkoutContent?.addEventListener('click',event => {
      const control = event.target.closest('[data-ivori-checkout-adjust],[data-ivori-checkout-remove]');
      if (!control) return;
      const quantity = Number(control.closest('[data-ivori-checkout-key]').querySelector('[data-ivori-checkout-input]').value);
      changeCheckoutItem(control,control.matches('[data-ivori-checkout-remove]') ? 0 : Math.max(0,quantity + Number(control.dataset.ivoriCheckoutAdjust)));
    },options);
    checkoutContent?.addEventListener('change',event => {
      if (event.target.matches('[data-ivori-checkout-input]')) changeCheckoutItem(event.target,Number(event.target.value));
    },options);
    checkoutContent?.addEventListener('input',event => {
      if (event.target.matches('[data-ivori-checkout-input]')) {
        event.target.setCustomValidity('');
        renderCheckoutPrices();
      }
    },options);
    checkoutContent?.addEventListener('keydown',event => {
      if (event.key === 'Enter' && event.target.matches('[data-ivori-checkout-input]')) {
        event.preventDefault();
        event.target.blur();
      }
    },options);
    checkoutContent?.addEventListener('submit',event => {
      const checkoutForm = event.target;
      if (checkoutProceedForms.has(checkoutForm)) {
        checkoutProceedForms.delete(checkoutForm);
        return;
      }
      event.preventDefault();
      continueCheckout(checkoutForm);
    },options);
    buyNowButtons.forEach(button => button.addEventListener('click',async () => {
      if (checkoutOpening || checkoutBusy && !checkoutUpdating || submitting || !checkoutDialog || !checkoutContent) return;
      if (checkoutUpdating) {
        checkoutOpening = true;
        syncBuyNow();
        let saved = false;
        try {
          saved = await flushCheckoutEdits();
        } finally {
          checkoutOpening = false;
          syncBuyNow();
        }
        if (!saved || controller.signal.aborted) return;
      }
      if (offers.length) updateOffer();
      else if (headPage) updateHeadPage();
      if (!add || add.disabled) return;
      const items = purchaseItems();
      if (!items.length) return;
      const signature = JSON.stringify(items);
      checkoutOpener = button;
      checkoutDialog.classList.remove('ivori-add-to-cart-drawer');
      checkoutOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      syncCheckoutViewport();
      checkoutDialog.showModal();
      const alreadyPrepared = preparedSelection === signature;
      showCheckoutLoading();
      checkoutContent.setAttribute('aria-busy','true');
      checkoutBusy = true;
      submitting = true;
      if (offers.length) updateOffer();
      else if (headPage) updateHeadPage();
      syncBuyNow();
      try {
        await window.IvoriPacks?.migrate();
        const response = alreadyPrepared
          ? await fetch(`${root.dataset.cartUrl}?sections=ivori-quick-checkout,cart-icon-bubble`,{signal:controller.signal})
          : await fetch(root.dataset.cartAddUrl,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items,sections:['ivori-quick-checkout','cart-icon-bubble'],sections_url:root.dataset.cartUrl}),signal:controller.signal});
        const payload = await response.json();
        const result = alreadyPrepared ? {sections:payload} : payload;
        if (!response.ok) throw new Error(result.description || result.message || checkoutText.error);
        preparedSelection = signature;
        const iconUpdated = updateCartIcon(result.sections);
        const section = result.sections?.['ivori-quick-checkout'];
        const rendered = section && new DOMParser().parseFromString(section,'text/html').querySelector('[data-ivori-checkout-order]');
        if (rendered?.querySelector('form')) checkoutContent.replaceChildren(rendered);
        else await loadCheckoutFallback();
        if (rendered?.querySelector('form') && !iconUpdated) {
          try {
            const cartResponse = await fetch(`${root.dataset.cartUrl}.js`,{headers:{'Accept':'application/json'},signal:controller.signal});
            if (cartResponse.ok) updateCartCount(await cartResponse.json());
          } catch (error) { /* Keep the ready checkout available if the header refresh fails. */ }
        }
        notifyCheckoutCart(result);
      } catch (error) {
        if (error.name !== 'AbortError') {
          const message = checkoutElement('p','ivori-checkout-error',error.message || checkoutText.error);
          message.setAttribute('role','alert');
          checkoutContent.replaceChildren(message);
        }
      } finally {
        checkoutBusy = false;
        submitting = false;
        checkoutContent.setAttribute('aria-busy','false');
        if (checkoutStatus) checkoutStatus.textContent = '';
        if (offers.length) updateOffer();
        else if (headPage) updateHeadPage();
        syncBuyNow();
      }
    },options));
    checkoutDialog?.addEventListener('click',event => {
      if (event.target === checkoutDialog || event.target.closest('[data-ivori-checkout-close]')) checkoutDialog.close();
    },options);
    checkoutDialog?.addEventListener('close',() => {
      if (checkoutHandoff) setCheckoutHandoff(false);
      notifyClosedCheckout();
      if (checkoutOverflow !== undefined) document.body.style.overflow = checkoutOverflow;
      checkoutOverflow = undefined;
      checkoutDialog.classList.remove('ivori-add-to-cart-drawer');
      clearAddConfirmation();
      syncBuyNow();
      if (root.isConnected) checkoutOpener?.focus({preventScroll:true});
    },options);
    window.addEventListener('pageshow',event => {
      if (event.persisted && checkoutHandoff) setCheckoutHandoff(false);
      updateOpenCheckoutViewport();
    },options);
    const buyNowObserver = add && 'MutationObserver' in window ? new MutationObserver(syncBuyNow) : null;
    buyNowObserver?.observe(add,{attributes:true,attributeFilter:['disabled']});
    syncBuyNow();
    const sticky = root.querySelector('[data-ivori-sticky]');
    const buy = root.querySelector('.ivori-buy');
    const purchasePanel = root.querySelector('[data-ivori-purchase-panel]');
    const purchaseAdd = root.querySelector('[data-ivori-purchase-add]');
    const purchaseAddLabel = root.querySelector('[data-ivori-purchase-add-label]');
    let purchaseScrollFrame = 0;
    function syncPurchaseAdd() {
      if (!purchaseAdd || !add) return;
      purchaseAdd.disabled = add.disabled;
      if (purchaseAddLabel) purchaseAddLabel.textContent = add.querySelector('[data-ivori-add-label]')?.textContent || addText;
    }
    function updatePurchaseFade() {
      if (!purchasePanel || !buy) return;
      purchasePanel.dataset.canScroll = String(buy.scrollHeight - buy.clientHeight - buy.scrollTop > 2);
    }
    function schedulePurchaseFade() {
      cancelAnimationFrame(purchaseScrollFrame);
      purchaseScrollFrame = requestAnimationFrame(updatePurchaseFade);
    }
    purchaseAdd?.addEventListener('click', () => add?.click(), options);
    const purchaseButtonObserver = purchaseAdd && add && 'MutationObserver' in window ? new MutationObserver(syncPurchaseAdd) : null;
    purchaseButtonObserver?.observe(add, {attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true});
    const purchaseResizeObserver = purchasePanel && buy && 'ResizeObserver' in window ? new ResizeObserver(schedulePurchaseFade) : null;
    if (purchasePanel && buy) {
      buy.addEventListener('scroll', schedulePurchaseFade, {...options, passive: true});
      root.addEventListener('change', schedulePurchaseFade, options);
      window.addEventListener('resize', schedulePurchaseFade, options);
      purchaseResizeObserver?.observe(buy);
      [...buy.children].forEach(child => purchaseResizeObserver?.observe(child));
      schedulePurchaseFade();
    }
    syncPurchaseAdd();
    const siteFooter = document.querySelector('footer.footer');
    let buyHasPassed = false;
    let siteFooterVisible = false;
    function updateStickyVisibility() {
      sticky?.classList.toggle('is-visible', buyHasPassed && !siteFooterVisible);
    }
    const purchaseObserver = sticky && buy && 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      buyHasPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      updateStickyVisibility();
    }) : null;
    const footerObserver = sticky && siteFooter && 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      siteFooterVisible = entry.isIntersecting;
      updateStickyVisibility();
    }, {rootMargin: '0px 0px 112px 0px'}) : null;
    footerObserver?.observe(siteFooter);
    purchaseObserver?.observe(buy);
    stickyAdd?.addEventListener('click', () => {
      if ((offers.length || headPage) && add?.disabled) {
        buy?.scrollIntoView({behavior: motion(), block: 'start'});
        offers.find(radio => radio.checked)?.focus({preventScroll: true});
      } else add?.click();
    }, options);
    return () => {
      if (checkoutDialog?.open) checkoutDialog.close();
      if (checkoutOverflow !== undefined) document.body.style.overflow = checkoutOverflow;
      buyNowObserver?.disconnect();
      if (dialog?.open) dialog.close();
      if (pageOverflow !== undefined) document.body.style.overflow = pageOverflow;
      pageOverflow = undefined;
      clearTimeout(checkoutUpdateTimer);
      controller.abort();
      motionImages.forEach(state => {stopMotionVideo(state.video, true);state.video?.remove();});
      stopMotionVideo(viewerVideo, true);
      viewerVideo?.remove();
      motionObserver?.disconnect();
      cancelAnimationFrame(scrollFrame);
      clearTimeout(scrollSettleTimer);
      resizeObserver?.disconnect();
      viewerResizeObserver?.disconnect();
      purchaseObserver?.disconnect();
      footerObserver?.disconnect();
      purchaseButtonObserver?.disconnect();
      purchaseResizeObserver?.disconnect();
      cancelAnimationFrame(purchaseScrollFrame);
    };
  }
  customElements.define('ivori-product-page', class extends HTMLElement {
    connectedCallback() {
      this.ivoriFrame = requestAnimationFrame(() => {
        if (this.isConnected && !this.ivoriCleanup) this.ivoriCleanup = initialize(this);
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.ivoriFrame);
      this.ivoriCleanup?.();
      this.ivoriCleanup = null;
    }
  });
})();


